import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  AlertIcon,
  ChevronIcon,
  PencilIcon,
  PlusIcon,
  SignatureIcon,
  TrashIcon,
} from '@/components/catalog/icons';
import { ImportedFromXeroBadge } from '@/components/documents/ImportedFromXeroBadge';
import { CreditedTo } from '@/components/documents/CreditedTo';
import { InlineQuoteFields } from '@/components/quotes/InlineQuoteFields';
import { QuoteFormModal } from '@/components/quotes/QuoteFormModal';
import {
  QuoteLineItemsTable,
  type CompanySalesTax,
} from '@/components/quotes/QuoteLineItemsTable';
/*
 * ⚠️ **The ONLY route to `POST /quotes/{id}/invoices`** — billing a quote a line
 * at a time, which until now was live on the server and reachable from nowhere
 * in this app. It is deliberately NOT gated on `billingMode`: the handler never
 * reads that column, so a partial raise is legitimate on any quote. See the
 * modal's header comment.
 */
import { BillQuoteLinesModal } from '@/components/quotes/BillQuoteLinesModal';
import { ExpiryBadge, QuoteIcon, StageBadge } from '@/components/quotes/QuoteBits';
import { InvoiceMoney, StatusBadge } from '@/components/invoices/InvoiceBits';
/*
 * ⚠️ Convert OPENS THIS rather than firing the request, on the owner's
 * instruction: an invoice is a real financial record and nothing here creates
 * one until somebody has read the prefilled form and pressed Save. The modal
 * owns the whole conversion from that point — the `POST /convert`, the follow-up
 * PATCH, both 409s and the partial-failure report — so this page holds only the
 * open/closed state and the navigation away.
 */
import { InvoiceFormModal } from '@/components/invoices/InvoiceFormModal';
import { ActivityButton, ActivityDrawer } from '@/components/tasks/ActivityDrawer';
import { EnvelopePanel } from '@/components/docusign/EnvelopePanel';
import { SendForSignatureModal } from '@/components/docusign/SendForSignatureModal';
import { SigningLinkDialog } from '@/components/docusign/SigningLinkDialog';
import { VoidEnvelopeDialog } from '@/components/docusign/VoidEnvelopeDialog';
import { ChangeSignerDialog } from '@/components/docusign/ChangeSignerDialog';
/*
 * Self-contained: it owns its own request, in-flight guard, cooldown countdown
 * and outcome wording. Mounted, never re-implemented — a second sentence here
 * about what a `200` from a reminder means would be a second thing to drift
 * from the one that already exists.
 */
import { RemindEnvelopeControl } from '@/components/docusign/RemindEnvelopeControl';
import { isEmbeddedDelivery, type SignerValue } from '@/lib/docusign';
/*
 * The header's own read of the envelopes. It shares `envelopeReloadKey` with
 * `EnvelopePanel` below, so the two move together: anything that makes the
 * panel re-read makes the header re-decide, and neither can sit on a list the
 * other has already replaced.
 */
import { useQuoteEnvelopes, type QuoteEnvelopesState } from '@/lib/use-quote-envelopes';
import { DownloadPdfButton } from '@/components/documents/DownloadPdfButton';
/*
 * The in-app PDF viewer. ONE host per page (`useQuotePdfPreview`), opened by the
 * header button and by `?preview=1`; the viewer owns its own request, refusals,
 * Mark Sent and "Send to Xero and preview".
 */
import {
  PreviewQuotePdfButton,
  useQuotePdfPreview,
  type QuotePdfPreviewTarget,
} from '@/components/quotes/QuotePdfPreview';
import { RefusalBanner, XeroPanel } from '@/components/xero/XeroPanel';
import { XeroSalesDefaultsDialog } from '@/components/xero/XeroSalesDefaultsDialog';
import { STAGED_CONVERT_REASON, hasHandEnteredTotals } from '@/lib/quote-utils';
import { ApiError, api } from '@/lib/api';
import {
  exchangeRateOf,
  formatRateLine,
  toBaseCurrency,
  type ExchangeRate,
} from '@/lib/currency-conversion';
import { dealValue as resolveDealValue } from '@/lib/deals';
import { denialReason } from '@/lib/permissions';
/*
 * The "the customer's copy shows a different total" fact. ONE module for both
 * detail pages — read its header before touching the wording, and never test
 * anything but `repricedAt` to decide whether to render it.
 */
import { repriceNotice } from '@/lib/reprice';
import { formatDateOnly, formatDateTime, formatDecimalString, pluralise, documentDisplayTitle} from '@/lib/format';
import { documentCustomerName } from '@/lib/company-names';
import { attachmentsSentence } from '@/lib/record-delete';
import { viewerZone } from '@/lib/task-time';
import type {
  Company,
  DocuSignEnvelope,
  Invoice,
  Quote,
  QuoteProduct,
  UserSummary,
} from '@/lib/types';
import { useAuth } from '@/lib/use-auth';
import { cityLabel } from '@/lib/use-project-options';
import {
  autoPushOutcomeOf,
  linkOf,
  type XeroAutoPushOutcome,
  type XeroLink,
} from '@/lib/xero';
import { useXero } from '@/lib/xero-context';
import { usePageLayout } from '@/lib/use-page-layout';
import { LayoutGrid, LayoutToolbar } from '@/components/layout';
import {
  Avatar,
  Badge,
  Button,
  ConfirmDialog,
  EmptyState,
  Modal,
  PageHeader,
  Panel,
  PanelBody,
  PanelFooter,
  PanelHeader,
  Skeleton,
  Table,
  TableWrap,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/components/ui';

/* --- small shared bits --------------------------------------------------- */

/*
 * ⚠️ **`QUOTE_IS_STAGED_CODE` and `STAGED_CONVERT_REASON` LIVE IN
 * `@/lib/quote-utils` NOW**, and were declared here until the Convert control
 * became a modal. They had to move: `InvoiceFormModal` is the third reader of
 * both — it is what Convert opens, and it is where the 409 now surfaces — and a
 * component importing a page is an import cycle rather than a matter of taste.
 * Do not re-declare either here; a second copy of that sentence is a wording
 * that gets corrected in one place and not the other, with nothing failing.
 */

function BackToQuotes() {
  return (
    <Link
      to="/quotes"
      className="inline-flex items-center gap-1 rounded-sm text-xs text-fg-muted transition-colors duration-150 ease-out hover:text-fg motion-reduce:transition-none"
    >
      <ChevronIcon direction="left" />
      All quotes
    </Link>
  );
}

function DetailItem({ label, hint, children }: { label: string; hint?: ReactNode; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-2xs font-semibold tracking-[0.06em] text-fg-subtle uppercase">{label}</dt>
      <dd className="mt-0.5 text-sm text-fg-secondary [overflow-wrap:anywhere]">
        {children}
        {hint && <span className="mt-0.5 block text-xs text-fg-muted">{hint}</span>}
      </dd>
    </div>
  );
}

function DetailGroup({
  title,
  columns = 'sm:grid-cols-2 lg:grid-cols-4',
  children,
}: {
  title: string;
  columns?: string;
  children: ReactNode;
}) {
  return (
    <PanelBody className="border-b border-border last:border-b-0">
      <h3 className="mb-3 text-2xs font-semibold tracking-[0.06em] text-fg-subtle uppercase">
        {title}
      </h3>
      <dl className={`grid gap-x-4 gap-y-3 ${columns}`}>{children}</dl>
    </PanelBody>
  );
}

/*
 * Every relationship on a quote is a LEFT JOIN and every one of them is null on
 * all three Zoho imports — the export simply had no column for them. That is an
 * ordinary, expected state, not a data-integrity event, so it reads as
 * "Unassigned" rather than as an error.
 *
 * `to` is deliberately built by the CALLER and only when the embed exists. A
 * template like `/companies/${quote.company?.id}` produces the literal string
 * "/companies/undefined" for every one of those imports, and the moment such a
 * value becomes clickable it is a broken route — so this component takes an
 * optional `to` and refuses to link without one.
 */
function LinkedValue({ to, name }: { to?: string; name?: string | null }) {
  if (!to || !name) return <span className="text-fg-subtle">Unassigned</span>;
  return (
    <Link
      to={to}
      className="rounded-sm text-accent-brand transition-colors duration-150 ease-out hover:underline motion-reduce:transition-none"
    >
      {name}
    </Link>
  );
}

/**
 * A `d_users` row — SEED EMERGE staff. Never a contact.
 *
 * `name` is typed as a string but arrives `''` on real rows, so it is not a
 * usable label on its own: rendering it bare puts a real, assigned person on
 * screen as a blank, which reads as unassigned. Fall back to the email, then to
 * the id — a person we cannot name is still a person, and saying so is the only
 * honest rendering. Only a genuinely absent record says "Unassigned".
 */
function StaffValue({ person }: { person: UserSummary | null }) {
  if (!person) return <span className="text-fg-subtle">Unassigned</span>;
  const label = person.name.trim() || person.email.trim() || `Staff #${person.id}`;
  return (
    <span className="flex min-w-0 items-center gap-2">
      <Avatar name={label} size="sm" />
      <span className="truncate">{label}</span>
    </span>
  );
}

/*
 * ⚠️ **The read-only basket that used to live here is GONE, and its helpers with
 * it** — `Percent`, `LineCity`, `LineInclusions`, `lineLabel`, `GeneratedMoney`,
 * `BasketHead` and `BasketSkeleton`.
 *
 * `QuoteLineItemsTable` is the line-items feature: it fetches the lines, edits
 * them in place, owns its own three states and its own delete confirmation. It
 * existed in this repo, fully written, and was **imported by nothing** — the
 * page went on rendering a second, read-only copy of the same data beside a
 * modal that edited one row at a time. Two surfaces over one collection is how
 * the two drift; this page now mounts the component and renders no table of its
 * own.
 *
 * The page still fetches the basket itself, and that is not a duplicate for its
 * own sake — four decisions here need the lines and none of them is the table:
 * the hand-entered-totals guard, `hasLineItems` on the quote form, the line
 * count in the delete confirmation, and the "add a line" gate below.
 */

/**
 * A header total rendered inside a confirmation dialog, with its currency
 * spelled out beside it.
 *
 * Never `?? '0.00'`: `discountTotal` and `taxTotal` are nullable and are null on
 * every imported quote, and "not recorded" is a different thing to tell someone
 * who is about to overwrite these figures than "zero".
 */
function TotalsRow({
  label,
  value,
  currency,
  strong = false,
}: {
  label: string;
  value: string | null;
  currency: string | null;
  strong?: boolean;
}) {
  return (
    <li className="flex items-baseline justify-between gap-4">
      <span className="text-fg-muted">{label}</span>
      <span className={`tabular text-fg ${strong ? 'font-semibold' : 'font-medium'}`}>
        {value === null
          ? 'Not recorded'
          : `${formatDecimalString(value)}${currency ? ` ${currency}` : ''}`}
      </span>
    </li>
  );
}

/** All four header totals, as both hazard dialogs need to name all four. */
function HeaderTotals({ quote }: { quote: Quote }) {
  return (
    <ul className="m-0 mt-3 list-none space-y-1 rounded-md border border-border bg-surface-sunken px-3 py-2 text-sm">
      <TotalsRow label="Sub total" value={quote.subTotal} currency={quote.currency} />
      <TotalsRow label="Discount total" value={quote.discountTotal} currency={quote.currency} />
      <TotalsRow label="Tax total" value={quote.taxTotal} currency={quote.currency} />
      <TotalsRow label="Grand total" value={quote.grandTotal} currency={quote.currency} strong />
    </ul>
  );
}

/* --- the base-currency figure, and the rate it rests on ------------------ */

/*
 * WHAT THIS QUOTE IS WORTH IN THE XERO ORGANISATION'S OWN CURRENCY.
 *
 * Xero renders a foreign-currency document as two figures and one sentence —
 * the document's own total, the same total in the organisation's base currency,
 * and the rate joining them. The owner asked for the same here.
 *
 * ⚠️ **Nothing in this file computes money.** `toBaseCurrency` in
 * `src/lib/currency-conversion.ts` does the arithmetic, on decimal strings. A
 * figure worked out here would mean `Number()` on money, which this repo forbids
 * everywhere else for the reason that binary floats lose pennies silently.
 *
 * ⚠️ **A stored rate is a HISTORICAL FACT, not a live one.** `currencyRate` is
 * the rate this quote is PINNED to; a quote sent in March keeps March's rate.
 * Rendering today's rate over it would silently restate what a customer was
 * quoted — the same failure shape as a project's derived cities, where removing
 * a city stops an invoice paid last March from reporting it. That is also why
 * this page offers no "refresh the rate" control: there is nothing here to
 * refresh, and a control that implied otherwise would invite someone to rewrite
 * a sent document's arithmetic.
 *
 * Five outcomes, kept apart because they say five different things — and THREE
 * of them must show no number at all:
 *
 *  - `none`          — nothing to say. Either the quote is already in the base
 *                      currency, where a second identical figure is noise, or
 *                      the base currency is UNKNOWN because Xero is not
 *                      connected. ⚠️ Never assume GBP: the tenant reports its
 *                      own base currency and this app must not guess one.
 *  - `no-rate`       — no rate is pinned. This is EVERY quote in production
 *                      today. There is no second figure and there must not be
 *                      one: `0.00` would be a fabricated total, and falling back
 *                      to a rate of 1 would assert that two currencies are at
 *                      parity.
 *  - `rate-only`     — a rate is pinned but the quote has no grand total to
 *                      apply it to. The rate is still a recorded fact.
 *  - `unusable-rate` — a rate is pinned that no figure can be derived from (a
 *                      stored `0.000000` divides by zero). Said plainly rather
 *                      than folded into `no-rate`, which would report the record
 *                      as empty when it is not.
 *  - `converted`     — both figures exist.
 */
type BaseCurrencyView =
  | { kind: 'none' }
  | { kind: 'no-rate'; baseCurrency: string }
  | { kind: 'unusable-rate'; baseCurrency: string; recorded: string }
  | { kind: 'rate-only'; rate: ExchangeRate }
  | { kind: 'converted'; rate: ExchangeRate; amount: string };

/*
 * ⚠️ **`exchangeRateOf` is the ONLY sanctioned way to build an `ExchangeRate`,
 * and this function does not hand-build one.** That module states it as a
 * contract: holding the type means "there is a real cross-currency conversion to
 * display", which is what lets `formatRateLine` and `toBaseCurrency` be total.
 * An `ExchangeRate` assembled here out of a raw `currencyRate` would slip a zero
 * or a malformed rate past the one check that exists, and `formatRateLine` would
 * dutifully print "1 GBP = 0 AUD" — a rate line that looks measured and is
 * arithmetic nonsense.
 *
 * The cost is that `exchangeRateOf` answers `null` for FIVE unrelated reasons,
 * and this page has to distinguish three of them, because they are the
 * difference between saying nothing and saying something. So the null case is
 * re-examined here, against the same inputs, purely to decide **what may be
 * said** — never to reconstruct a rate the module refused.
 */
function baseCurrencyView(quote: Quote, baseCurrency: string | null): BaseCurrencyView {
  const rate = exchangeRateOf({
    baseCurrency,
    documentCurrency: quote.currency,
    currencyRate: quote.currencyRate,
  });

  if (rate !== null) {
    /*
     * The GRAND total and nothing else. Xero converts the one figure at the foot
     * of the document, and `toBaseCurrency`'s own contract says why that is not
     * merely imitation: rounding once at the end and rounding each of four
     * totals can disagree by a penny, so a converted sub total, discount and tax
     * would not always add up to the converted grand total. Two columns that
     * fail to reconcile on a customer's document are worse than one column.
     */
    const amount = toBaseCurrency(quote.grandTotal, rate);
    /*
     * `null` here means the AMOUNT could not be converted, not that the rate was
     * bad — the rate has already passed `exchangeRateOf`. A quote with no grand
     * total is the realistic case. The rate is still a recorded fact and is
     * still worth showing; there is simply nothing to apply it to.
     */
    return amount === null ? { kind: 'rate-only', rate } : { kind: 'converted', rate, amount };
  }

  /*
   * Xero is disconnected, or the connection read has not settled, or the quote
   * records no currency. In all three the base currency is UNKNOWN, and the only
   * honest rendering of an unknown is silence. ⚠️ Never assume GBP because the
   * live tenant happens to use it.
   */
  if (baseCurrency === null || quote.currency === null) return { kind: 'none' };
  /*
   * Already in the base currency. Compared exactly, never case-folded, matching
   * `exchangeRateOf`: both sides are CHAR(3) uppercase and this app folds case
   * nowhere, so a difference in case would mean one of them is not what it
   * claims to be — worth showing rather than quietly absorbing.
   */
  if (baseCurrency === quote.currency) return { kind: 'none' };
  /*
   * Cross-currency, and no usable rate — the two cases that must NOT be
   * collapsed. `null` is the ordinary state and is every quote in production
   * today; a rate that is present but unusable is a fault in the record, and
   * reporting it as "not recorded" would send someone looking for a rate to
   * enter when one is already there.
   */
  if (quote.currencyRate === null) return { kind: 'no-rate', baseCurrency };
  return { kind: 'unusable-rate', baseCurrency, recorded: quote.currencyRate };
}

/*
 * ⚠️ Why `no-rate` says more than "Not recorded".
 *
 * Every quote in production carries `currencyRate: null`, so this is the branch
 * a reader will actually meet, and a bare "Not recorded" on every single quote
 * reads as missing data or a half-finished feature. The second clause exists to
 * stop that one wrong conclusion: nothing is missing, this is the ordinary
 * state, and the rate is Xero's to apply rather than ours to store. It states
 * what `Quote.currencyRate` documents, not a guess about Xero.
 *
 * The `rate-only` and `converted` clause earns its place the same way: without
 * it a reader takes the rate for a live one, and the absence of any refresh
 * control for a bug.
 */
function rateHint(view: BaseCurrencyView): string | null {
  switch (view.kind) {
    case 'none':
      return null;
    case 'no-rate':
      return `No rate pinned — Xero applies its own on the day, so no ${view.baseCurrency} figure.`;
    case 'unusable-rate':
      return `Rate unusable, so no ${view.baseCurrency} figure.`;
    case 'rate-only':
    case 'converted':
      return 'Pinned to this quote — not today’s rate.';
  }
}

/* --- loading shapes ------------------------------------------------------ */

function DetailsSkeleton() {
  return (
    <Panel>
      <PanelHeader title="Quote details" />
      <PanelBody>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 12 }, (_, i) => (
            <div key={i} className="grid gap-1.5">
              <Skeleton className="h-2.5 w-20" />
              <Skeleton className="h-3.5 w-32" />
            </div>
          ))}
        </div>
      </PanelBody>
    </Panel>
  );
}

/* --- the Xero link envelope --------------------------------------------- */

/*
 * ⚠️ **Read the link with `linkOf(quote)`, and never from `xeroQuoteId`.**
 * That column is writable and written by nothing, so `null` there is not
 * evidence of anything — see the long note at the call site below.
 *
 * This file used to carry a private `quoteXeroLink` — a runtime `'xero' in
 * quote` test with its own `isXeroLink` guard and status map — for one reason
 * only: `Quote` in `src/lib/types.ts` did not declare the key, so nothing else
 * compiled. `Quote.xero` is now a REQUIRED `XeroLink`, so the workaround is
 * gone and `linkOf` is the single reader. It still falls back to `NO_LINK`, so
 * a response that predates the envelope degrades to "no counterpart" rather
 * than crashing — the safe direction, because the resulting offer is a create,
 * which the server refuses with `XERO_ALREADY_LINKED` rather than duplicating.
 */

/* --- page ---------------------------------------------------------------- */

type LoadResult =
  | { kind: 'ready'; quote: Quote }
  | { kind: 'notfound' }
  | { kind: 'error'; message: string };

/**
 * WHICH signature control the page header offers, and it is at most one of two
 * shapes — never all three at once.
 *
 * Kept as a named function rather than inline in the JSX because it is the
 * thing most likely to drift from `EnvelopePanel`'s own answer, and a drift
 * here is silent: the header would offer a control the panel refuses two
 * screens down, or refuse one the panel offers.
 *
 * ⚠️ **`null` envelopes is NOT "no envelopes".** It is the read having failed
 * or not yet returned, and the two must never collapse: offering "Send for
 * signature" there raises a SECOND envelope for a quote that already has one
 * out with a customer — a real document, to a real signer, with nothing on
 * screen having looked wrong. So unknown yields `none`, exactly as `loading`
 * does.
 *
 * ⚠️ **`notWired` yields `none` as well, and renders NOTHING — not an error
 * and not a disabled button.** A 404 means the endpoint is not set up on the
 * API yet, which is this app's standing "not wired" reading; the panel below
 * already says so once, and a header is not the place to say it twice.
 *
 * The outstanding/terminal split mirrors the panel: `Send` is refused while an
 * envelope is live (one per quote), and "Change signer and resend" is refused
 * on a terminal one — so a quote whose only envelope was voided or completed
 * correctly offers Send again rather than a resend of something finished.
 */
type HeaderSignatureControl =
  | { kind: 'none' }
  | { kind: 'send' }
  | { kind: 'outstanding'; envelope: DocuSignEnvelope };

function headerSignatureControl(state: QuoteEnvelopesState): HeaderSignatureControl {
  if (state.notWired || state.loading || state.envelopes === null) return { kind: 'none' };
  const outstanding = state.envelopes.find((envelope) => !envelope.terminal);
  return outstanding ? { kind: 'outstanding', envelope: outstanding } : { kind: 'send' };
}

/** Roughly a 272px menu; clamped into the viewport before it is drawn. */
const SIGNATURE_MENU_WIDTH = 272;
/**
 * Header plus three items, each able to carry a disabled sentence. Only used to
 * decide whether to open upwards, so over-estimating costs a flip that was not
 * needed while under-estimating opens it off the bottom of a phone screen.
 */
const SIGNATURE_MENU_EST_HEIGHT = 260;

const MENU_ITEMS = '[role="menuitem"]';

/**
 * The three signature actions, behind ONE control in the page header.
 *
 * ⚠️ **A menu rather than three buttons, and the arithmetic is why.** The
 * header already carries Activity, Convert, Edit and Delete for a
 * full-permission user; at 320px a `md` button is roughly one per line, and
 * "Change signer and resend…" alone is ~201px against ~288px usable. Three more
 * would stack the header ~300px deep before the quote's own content began.
 *
 * ⚠️ **`PageHeader`'s actions slot wraps** (`flex shrink-0 flex-wrap`) — unlike
 * `PanelHeader`'s, whose no-wrap rule does NOT transfer here. But `shrink-0`
 * defeats the wrap at `sm` and above, where the outer container turns to
 * `sm:flex-row`: a wrapping flex container's max-content is every child on one
 * line, so the row does not wrap in row mode and the pressure lands on the
 * quote's `h1`, which carries `truncate` and silently collapses to an ellipsis.
 * One control is what keeps that from happening.
 *
 * ⚠️ **All three items always render, disabled with a reason rather than
 * hidden.** The reader has deliberately opened a menu to find them, and an item
 * absent from a menu somebody went looking in is indistinguishable from a
 * broken page. (Contrast the Xero `sync` rule, where the affordance must be
 * ABSENT: there the only outcome is an error for everybody — a different
 * question from a refusal about THIS reader or THIS envelope.)
 *
 * The popover is portalled to `document.body`, positioned `fixed`, on layer
 * rung **60**. Read the ladder at the top of `src/index.css` before changing
 * that number: the header sits inside page content, and a `z-` left there would
 * be clipped by it — while a number inside the sticky bar's stacking context is
 * LOCAL and would change nothing on screen.
 */
function SignatureMenu({
  control,
  quoteName,
  canManage,
  manageDenial,
  hasCompany,
  busy,
  onSend,
  onChangeSigner,
  onReminded,
}: {
  control: HeaderSignatureControl;
  quoteName: string;
  canManage: boolean;
  manageDenial: string | null;
  /**
   * Whether the quote names a company. `ChangeSignerDialog`'s signer picker is
   * scoped to it, and the VOID runs first — so without one the only outcome is
   * a destroyed request and an empty picker.
   */
  hasCompany: boolean;
  busy: boolean;
  onSend: () => void;
  onChangeSigner: (envelope: DocuSignEnvelope) => void;
  onReminded: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState({
    left: 0,
    top: 0,
    width: SIGNATURE_MENU_WIDTH,
    flip: false,
  });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const place = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const box = el.getBoundingClientRect();
    const width = Math.min(SIGNATURE_MENU_WIDTH, window.innerWidth - 16);
    const below = window.innerHeight - box.bottom;
    const flip = below < SIGNATURE_MENU_EST_HEIGHT && box.top > below;
    /*
     * Right-aligned to the trigger, then clamped. The trigger sits at the right
     * edge of the header, so a left-aligned menu would hang off a 320px screen —
     * and being `fixed`, that overhang adds no page scroll, so those items would
     * be silently unreachable rather than visibly wrong.
     */
    const maxLeft = Math.max(8, window.innerWidth - width - 8);
    const left = Math.min(Math.max(box.right - width, 8), maxLeft);
    setRect({ left, top: flip ? box.top : box.bottom, width, flip });
  }, []);

  useLayoutEffect(() => {
    if (open) place();
  }, [open, place]);

  useEffect(() => {
    if (!open) return;
    const onMove = () => place();
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [open, place]);

  useEffect(() => {
    if (!open) return;
    menuRef.current?.querySelector<HTMLElement>(MENU_ITEMS)?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: PointerEvent) {
      const target = event.target as Node;
      if (menuRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
      setOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  function close(returnFocus = true) {
    setOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  }

  function onTriggerKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setOpen(true);
    } else if (event.key === 'Escape' && open) {
      event.preventDefault();
      setOpen(false);
    }
  }

  function onMenuKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Escape') {
      event.preventDefault();
      // Don't let one Escape close this and whatever else is dismissable.
      event.stopPropagation();
      close();
      return;
    }

    const items = Array.from(menuRef.current?.querySelectorAll<HTMLElement>(MENU_ITEMS) ?? []);
    if (items.length === 0) return;

    if (event.key === 'Tab') {
      // Portalled, this is nowhere near its trigger in the tab order, so an
      // uncaught Tab would strand focus at the end of the document.
      const first = items[0];
      const last = items[items.length - 1];
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
      return;
    }

    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const current = items.indexOf(document.activeElement as HTMLElement);
    const next =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? items.length - 1
          : event.key === 'ArrowDown'
            ? (current + 1) % items.length
            : (current - 1 + items.length) % items.length;
    items[next]?.focus();
  }

  /*
   * ⚠️ The whole control is absent while the envelopes are unknown — see
   * `headerSignatureControl`. `null` is not "no envelopes", and a Send offered
   * over an unknown list raises a SECOND request to a real customer.
   */
  if (control.kind === 'none') return null;

  const envelope = control.kind === 'outstanding' ? control.envelope : null;

  const sendDenial = !canManage
    ? manageDenial
    : envelope
      ? 'A signature request is already outstanding on this quote. Resend it, or change the signer, instead.'
      : null;

  /*
   * ⚠️ Answered from `terminal`, the server's own flag, never from the status
   * NAME — this codebase has been bitten three times by matching on an
   * admin-editable string.
   *
   * ⚠️ **No company is a REFUSAL here, not a confirmation.** The dialog voids
   * first and its signer picker is scoped to the company, so offering this on a
   * company-less quote buys a destroyed request and an empty picker — the quote
   * left with no signature request at all. Not offering an action whose only
   * outcome is destruction beats confirming it.
   */
  const changeSignerDenial = !canManage
    ? manageDenial
    : !envelope
      ? 'There is no outstanding signature request to re-point. Send one instead.'
      : !hasCompany
        ? 'This quote names no company, so there is nobody to choose as the new signer. Add a company to the quote first.'
        : null;

  const triggerLabel = `Signature actions for ${quoteName}`;

  return (
    <>
      <Button
        ref={triggerRef}
        variant="secondary"
        /* Default `md`: the neighbours in this row take the default too, and a
           `sm` control beside them mixes heights visibly. */
        leadingIcon={<SignatureIcon />}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? 'quote-signature-menu' : undefined}
        aria-label={triggerLabel}
        onClick={() => setOpen((prev) => !prev)}
        onKeyDown={onTriggerKeyDown}
      >
        Signature
      </Button>

      {open &&
        createPortal(
          <div
            ref={menuRef}
            onKeyDown={onMenuKeyDown}
            style={{
              position: 'fixed',
              left: rect.left,
              width: rect.width,
              ...(rect.flip ? { bottom: window.innerHeight - rect.top + 4 } : { top: rect.top + 4 }),
            }}
            className="z-[60] overflow-hidden rounded-lg border border-border bg-surface-raised p-1 shadow-lg"
          >
            {/* Outside `role="menu"`: a menu may only contain menu items, and
                prose inside one is read as an item with no role. */}
            <p className="truncate px-2 pt-1 pb-1 text-2xs font-semibold tracking-[0.08em] text-fg-subtle uppercase">
              Signature — {quoteName}
            </p>
            <div role="menu" id="quote-signature-menu" aria-label={triggerLabel}>
              <Button
                role="menuitem"
                variant="ghost"
                size="sm"
                className="w-full justify-start"
                disabled={sendDenial !== null || busy}
                onClick={() => {
                  setOpen(false);
                  onSend();
                }}
              >
                Send for signature
              </Button>
              {/*
                The reason as TEXT, not a `title`: a disabled button has
                `pointer-events: none`, so its tooltip never reaches the person
                it was written for. A menu has the vertical room for it.
              */}
              {sendDenial && (
                <p className="m-0 px-2.5 pb-1 text-2xs text-fg-subtle [overflow-wrap:anywhere]">
                  {sendDenial}
                </p>
              )}

              {/*
                ⚠️ Self-contained: it owns its request, its in-flight guard, its
                cooldown countdown and its own outcome wording, and renders its
                own disabled reason. Nothing is added beside it — a second
                sentence about what a `200` from a reminder means would be a
                second thing to drift from the one that already exists.
              */}
              {envelope ? (
                <RemindEnvelopeControl
                  envelope={envelope}
                  canManage={canManage}
                  manageDenial={manageDenial}
                  variant="menu"
                  busy={busy}
                  onReminded={onReminded}
                />
              ) : (
                <>
                  <Button
                    role="menuitem"
                    variant="ghost"
                    size="sm"
                    className="w-full justify-start"
                    disabled
                  >
                    Resend for signature
                  </Button>
                  <p className="m-0 px-2.5 pb-1 text-2xs text-fg-subtle [overflow-wrap:anywhere]">
                    There is no outstanding signature request to resend.
                  </p>
                </>
              )}

              {/*
                ⚠️ **The ellipsis is load-bearing**: this opens a dialog and
                voids nothing on its own. A bare verb on an operation this
                destructive invites a press from somebody who thinks they can
                still back out — and they can, but only because the dialog
                exists, and that dialog carries its own confirmation.
              */}
              <Button
                role="menuitem"
                variant="ghost"
                size="sm"
                className="w-full justify-start"
                disabled={changeSignerDenial !== null || busy}
                onClick={() => {
                  setOpen(false);
                  if (envelope) onChangeSigner(envelope);
                }}
              >
                Change signer and resend…
              </Button>
              {changeSignerDenial && (
                <p className="m-0 px-2.5 pb-1 text-2xs text-fg-subtle [overflow-wrap:anywhere]">
                  {changeSignerDenial}
                </p>
              )}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}

export function QuoteDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  /*
   * ⚠️ **No argument.** `useSearchParams`' first parameter is a *default init*,
   * not options — `useSearchParams({ replace: true })` writes a literal
   * `replace=true` into the query string. The `{ replace: true }` this page does
   * want belongs on the SETTER, below.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const { ability, user } = useAuth();

  /*
   * The read gate, computed here because the return that uses it has to sit
   * below every hook. See that return, further down, for the reasoning.
   */
  const canReadModule = ability.canRead('quotes');

  /*
   * Read once, app-wide, by `XeroProvider`. A missing provider degrades to a
   * disconnected reading rather than throwing, so nothing on this page depends
   * on the integration being mounted.
   */
  const { connection: xeroConnection } = useXero();

  /*
   * The person's own arrangement of the page's sections (the layout manager).
   * Called here, above every early return. The defaults are today's order, all
   * full width, so nobody sees a change until they press Edit layout. The ids
   * are stored per user: never rename one.
   */
  const layout = usePageLayout('quote', [
    { id: 'details', label: 'Quote details' },
    { id: 'lines', label: 'Line items' },
    { id: 'signature', label: 'Signature' },
    { id: 'invoices', label: 'Invoices raised from this quote' },
    { id: 'xero', label: 'Xero' },
  ]);

  /*
   * A non-numeric path segment can never match a record, so it is a 404 without
   * asking the API — which would answer 422 for it anyway, and a validation
   * error is not what "/quotes/banana" means to a user.
   */
  const quoteId = id !== undefined && /^\d+$/.test(id) ? Number(id) : null;

  /*
   * One id-stamped slot for the header rather than separate loading / error /
   * notFound flags: navigating straight from one quote to another can never
   * flash the previous record's totals under the new id, and "loading" is
   * simply the absence of a result for this id.
   */
  const [result, setResult] = useState<{ id: number; value: LoadResult } | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  /*
   * The basket carries its own loading and error state, stamped with the same
   * id. It is a second request and it can fail on its own; when it does, the
   * header — which is where the money everybody came for actually lives — must
   * still render.
   */
  const [linesResult, setLinesResult] = useState<{
    id: number;
    lines: QuoteProduct[] | null;
    error: string | null;
  } | null>(null);

  /*
   * The invoices raised from this quote are a third, independent request
   * against a different collection, with the same id stamp. It can fail on its
   * own too and its failure must not take the quote down — but it also gates
   * Convert, so "unknown" is tracked apart from "none" further down.
   */
  const [invoicesResult, setInvoicesResult] = useState<{
    id: number;
    invoices: Invoice[] | null;
    total: number | null;
    error: string | null;
  } | null>(null);

  const [editing, setEditing] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(false);
  /*
   * THE HAND-ENTERED-TOTALS GATE, and why it is a piece of state rather than a
   * disabled button.
   *
   * A quote with header totals and no lines had those figures TYPED onto it.
   * The first line item makes the server rewrite all four from the lines, in
   * the same transaction, with no confirmation and no undo — and the typed ones
   * are kept nowhere. The line editor is an inline grid with its own "Add row"
   * button, so there is no single call site left to intercept; the gate is
   * therefore in front of the whole grid, and acknowledging it is what mounts
   * the editable table.
   *
   * It is deliberately NOT persisted. A reload asks again, which is the right
   * direction to be wrong in for a one-way overwrite.
   */
  const [pendingTotalsAdd, setPendingTotalsAdd] = useState(false);
  const [totalsAcknowledged, setTotalsAcknowledged] = useState(false);
  const [pendingConvert, setPendingConvert] = useState(false);
  /*
   * PARTIAL BILLING — `POST /quotes/{id}/invoices`, the other way an invoice is
   * raised from this quote, and it is not a variant of Convert.
   *
   * Convert bills the whole document once and is spent afterwards; this bills
   * chosen LINES and is repeatable until every line is taken. ⚠️ It is **not**
   * gated on `billingMode`, unlike Convert — the handler never selects that
   * column, so a `full` quote and an already-converted quote may both be
   * part-billed. A staged quote is the case that has no OTHER billing route, not
   * the only case this applies to.
   *
   * The modal owns the whole request: the line read, the selection, the optional
   * subject and dates, and every refusal — none of which carries a distinct
   * `error.code`, so nothing here tries to classify one.
   */
  const [pendingPartialBill, setPendingPartialBill] = useState(false);

  /*
   * THE SIGNATURE SURFACE — three dialogs and one panel, and the panel owns none
   * of them.
   *
   * `EnvelopePanel` reads the envelopes and asks for actions through callbacks;
   * sending, minting a link and voiding are separate screens with their own
   * validation. A callback that is not passed means the panel does not offer the
   * control at all — so all three ARE passed, unconditionally, and the *gating*
   * is `canManage` + `manageDenial`. That distinction matters for a `viewer`:
   * withholding `onSend` would make the empty state say sending "is not
   * available from this screen", which is false — it is available from this
   * screen, to somebody else. The denial says which.
   *
   * ⚠️ **`GET /docusign/connection` is deliberately NOT read here.** It is
   * ADMIN ONLY while this page is open to every authenticated role, so on most
   * sessions the read is a 403 — one guaranteed refusal per quote view, in a
   * trail people read for security. It buys exactly one thing: the ability to
   * REMOVE the not-binding warning once production credentials land. Omitting
   * `connection` leaves `signaturesNotBinding(null)` answering `true`, so every
   * session warns in the unconfirmed wording. That is the fail-safe direction
   * and the one that gets noticed — after DocuSign Go Live somebody will ask why
   * this still warns, which is a far better failure than a page that quietly
   * stopped warning because a read succeeded. When Go Live lands, the fix is to
   * pass a connection here, not to suppress anything.
   */
  const [sendingSignature, setSendingSignature] = useState(false);
  const [signingLinkFor, setSigningLinkFor] = useState<DocuSignEnvelope | null>(null);
  const [voidingEnvelope, setVoidingEnvelope] = useState<DocuSignEnvelope | null>(null);
  /*
   * "Change signer and resend", opened from the HEADER.
   *
   * ⚠️ Two pieces of state for one operation, because it is two steps: the
   * dialog voids the live envelope, and the SEND that follows is a different
   * screen. `signatureSeed` carries the chosen signer across that gap.
   *
   * 🔴 **Dropping the seed is the failure this path exists to prevent.**
   * `SendForSignatureModal` seeds the company's primary contact into an
   * untouched signer field — very often the exact person somebody has just
   * decided not to send to — so opening it without `initialSigner` silently
   * undoes the whole operation while looking like it worked.
   */
  const [changingSignerFor, setChangingSignerFor] = useState<DocuSignEnvelope | null>(null);
  const [signatureSeed, setSignatureSeed] = useState<SignerValue | null>(null);
  /*
   * Bumped to make the panel re-read its list. The dialogs are not ours and
   * cannot hand the panel a row, so the server is asked again rather than the
   * page keeping a second copy of the envelopes that could disagree with it.
   */
  const [envelopeReloadKey, setEnvelopeReloadKey] = useState(0);
  /*
   * The SAME `envelopeReloadKey` the panel takes, deliberately: the header and
   * the panel are two readers of one list, and giving the header its own token
   * would let them disagree about whether an envelope is outstanding — the
   * header still offering Send after a send the panel has already seen.
   *
   * ⚠️ `quoteId` is `null` on a non-numeric path segment, and the pinned hook
   * signature takes a `number`. That route renders "Quote not found" well
   * before any of these controls, so the only cost is one request for an id
   * that cannot exist. If the hook grows a `number | null` parameter, this call
   * site should take it rather than keeping the fallback.
   */
  const envelopeState = useQuoteEnvelopes(quoteId ?? 0, envelopeReloadKey);
  const signatureControl = headerSignatureControl(envelopeState);

  /*
   * "Change signer and resend", AFTER the void has already happened — the one
   * handler for both routes into it: the panel's row control and the header
   * menu. Two copies would agree the day they were written and drift on the
   * first correction, and what would drift is whether the chosen signer
   * survives.
   *
   * ⚠️ **Two steps, and the second is the whole point of the first.** The void
   * is done; the SEND needs the document payload — anchor text, the Xero fetch
   * or an uploaded PDF — which only `SendForSignatureModal` knows.
   *
   * 🔴 **Dropping the seed is the failure this path exists to prevent.** That
   * modal seeds the company's primary contact into an untouched signer field —
   * very often the exact person somebody has just decided not to send to — so
   * opening it without `initialSigner` silently undoes the operation while
   * looking like it worked.
   */
  function afterSignerChanged(signer: SignerValue) {
    setChangingSignerFor(null);
    /*
     * Re-read as well as seed: the void is a real state change on the server,
     * and the header must stop offering Resend on an envelope that is now dead
     * before the send dialog opens on top of it.
     */
    setEnvelopeReloadKey((key) => key + 1);
    setSignatureSeed(signer);
    setSendingSignature(true);
  }

  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  /*
   * ⚠️ **`convertError` went with the confirmation dialog.** Every convert
   * refusal — both 409s, the 404, the 403 — is now raised, classified and
   * rendered inside `InvoiceFormModal`, which is where the request is made from.
   * A second copy of that state here would be a banner over a page that did not
   * make the call.
   */

  /*
   * THE XERO SALES DEFAULTS, which are a COMPANY record read from a quote page.
   *
   * A quote is refused with `XERO_NO_TAX_TYPE` or `XERO_NO_LINE_AMOUNT_TYPE`
   * until its customer carries both, and `XeroSalesDefaultsDialog` needs the
   * whole `Company` to seed and diff against — three of the fields it reads are
   * not on `quote.company`, which is a bare `{id, name}`.
   *
   * So it is fetched **when the dialog opens**, never on page load: every quote
   * view would otherwise pay for a request that almost none of them use, and
   * the freshest possible copy is the one taken at the moment somebody is about
   * to edit it.
   */
  const [defaultsOpen, setDefaultsOpen] = useState(false);
  const [defaultsResult, setDefaultsResult] = useState<{
    id: number;
    company: Company | null;
    error: string | null;
  } | null>(null);
  const [defaultsToken, setDefaultsToken] = useState(0);
  /*
   * What to say once the defaults are saved — and NOT a trigger to push again.
   *
   * The person asked for one push. A second one firing on its own is an action
   * they did not take, and if it were refused for a different reason the
   * message would land with nothing on screen explaining where it came from.
   */
  const [defaultsSaved, setDefaultsSaved] = useState<string | null>(null);

  /**
   * What the Xero push did on the last save — or `null` when there is nothing
   * to say about it.
   *
   * ⚠️ **Held on the PAGE and not in the form, because the form closes.** The
   * save is finished and the modal is gone by the time anyone reads this; the
   * report has to outlive the thing that produced it, and it has to sit beside
   * the Xero panel, which is the record of what is true now.
   *
   * Cleared when the form is opened again — a report about the previous save
   * standing over a new one is the same stale-banner problem `openConfirm`
   * clears in `XeroPanel`.
   *
   * ## ⚠️ CORRECTED 2026-08-30 — THIS PAGE NO LONGER PUSHES. THE SERVER DOES.
   *
   * A `pushQuoteToXero` callback used to sit here and fire `api.pushToXero`
   * after every header PATCH. It is deleted. The backend now pushes inside the
   * save itself and answers `xeroPush` alongside `data`, so this state is
   * filled from that report through `autoPushOutcomeOf` and nothing on this
   * page calls Xero on a save any more. Shipping both would have doubled the
   * Xero calls on every save against a tenant cap of 5,000 a day.
   *
   * **The RULE the deleted block existed to enforce is unchanged, and it is now
   * enforced in a better place.** Its doc said, in full:
   *
   * > *"Only ever an UPDATE. `pushIntentFor` is the single decision point and
   * > it answers `'create'` for an unlinked quote — which this **refuses**. A
   * > save must never put a new document into SEED's accounting system on
   * > somebody's behalf; creating stays a button a person presses, in the panel
   * > below."*
   *
   * ⚠️ **HALF OF THAT WAS OVERTURNED ON 2026-08-30 AND THE OTHER HALF STILL
   * HOLDS.** This paragraph ended:
   *
   * > *"What changed is WHO enforces it: the server does, on every writer …
   * > where the deleted code could only enforce it on one screen. The 'Send to
   * > Xero' button in `XeroPanel` below is still the only thing that CREATES,
   * > and it is deliberately untouched."*
   *
   * **The last sentence is now false in both of its claims.** The owner asked
   * whether the button could go, was told it was still the only path into Xero,
   * and then decided the other way: **a save CREATES the Xero quote as well as
   * updating it, and the button is removed.** `XeroPanel` renders no create for
   * a quote or an invoice — see `CREATED_BY_SAVING` there — so there is no
   * button below and no *"deliberate act"* left to defend.
   *
   * **What survives is the part that was never about the button:** the rule
   * lives on the SERVER, on every writer — this page, a line-item edit, an
   * importer, a script and any second client — where the deleted client code
   * could only ever have enforced it on one screen. The server is also where
   * the duplicate guard now lives, which is the whole reason removing the
   * button is safe.
   *
   * It also recorded a gap that is now closed: *"line-item edits do not reach
   * it at all … editing a line therefore leaves Xero's copy of that line stale
   * until the next header save."* A line write now pushes on the server like
   * any other save.
   *
   * ⚠️ **AND THE REPORTING GAP UNDER IT IS CLOSED TOO, 2026-08-30.** This used
   * to continue: *"Its `xeroPush` report is not yet surfaced on this page —
   * `QuoteLineItemsTable` does not carry one up — so a line edit reaches Xero
   * and says nothing here. That is a reporting gap, not the old behavioural
   * one."* The grid carries one up now, and so does `InlineQuoteFields`, so
   * **every writer on this page reports into this one state**: the Edit modal,
   * the inline header section and the line grid.
   *
   * ⚠️ **What the GRID hands up is not one save's report but a RUN's**, and the
   * rule is stated once, on `QuoteLineItemsSavedProps.onXeroPush`: the first
   * `failed` of the run, or nothing. A `pushed` or a `skipped` line edit sets
   * this back to `null` and says nothing — `skipped` being the ordinary answer
   * for every quote not yet in Xero, which is most of them.
   */
  const [xeroPush, setXeroPush] = useState<XeroAutoPushOutcome | null>(null);

  /*
   * Tasks and notes, and the one place in this API where nothing is gated.
   *
   * The task and note routers carry NO role guard at all — unlike every other
   * write here, convert included — so `viewer` may add both. Nothing below
   * consults `ability`, deliberately: reading every record and being unable to
   * note anything about it is not a tier anybody would accept.
   *
   * Both now live in `ActivityDrawer`, behind one button in the header. It owns
   * the fetching, the completion toggle, the delete and the note composer, so
   * this page holds nothing but whether the sheet is open — the drawer's
   * trigger even recounts itself on close.
   */
  const viewerId = user?.id ?? null;
  const viewerTimeZone = viewerZone(user?.timezone);
  const [activityOpen, setActivityOpen] = useState(false);

  /*
   * What the delete confirmation is allowed to promise, read only when that
   * dialog opens.
   *
   * Nothing on the page carries these counts any more, and the drawer does not
   * report its writes back here, so a figure cached at page load would be stale
   * in exactly the direction that understates what is about to be destroyed.
   */
  const [attachmentTotals, setAttachmentTotals] = useState<{
    tasks: number | null;
    notes: number | null;
    systemNotes: number;
  }>({ tasks: null, notes: null, systemNotes: 0 });

  /*
   * Each control asks the ability model for its own action rather than sharing
   * one admin flag, because the server does not treat them alike:
   *
   *  - create / update take a WRITE role — `requireRole(...WRITE_ROLES)` — so a
   *    basic_user and an advanced_user may both edit the quote and its lines;
   *  - CONVERT is also a write role, not admin. `POST /quotes/:id/convert` is
   *    guarded by `requireRole(...WRITE_ROLES)` at quotes.routes.ts:66. A
   *    handoff doc claims an advanced_user gets a 403 there; it is wrong, and
   *    hiding Convert from everyone below admin hid a capability people have;
   *  - DELETE alone is admin-tier, as every DELETE in the API is.
   */
  const canCreate = ability.canCreate('quotes');
  const canUpdate = ability.canUpdate('quotes');
  const canDelete = ability.canDelete('quotes');
  const canConvert = ability.canConvertQuote();
  /*
   * The TRUE reason a signature write is refused, from the ability model rather
   * than "administrators only" — which is wrong for a viewer and misleading for
   * a basic user.
   *
   * ⚠️ Named ONCE and shared by the header controls, `EnvelopePanel` and
   * `SigningLinkDialog`. It was spelled inline at each of those before; three
   * copies of one expression agree the day they are written and drift on the
   * first correction, and what would drift is the sentence telling somebody why
   * they cannot send a customer a document.
   */
  /*
   * 2026-10-02 (owner): DocuSign send / remind / void / refresh / signing link
   * and every Xero action on this quote are `quotes.send`, not `quotes.update`.
   * Every user of `manageDenial` below is one of those, so it follows the same
   * permission as `canSend`.
   */
  const canSend = ability.canSend('quotes');
  const manageDenial = denialReason(ability, 'send', 'quotes');
  /*
   * PARTIAL BILLING asks a DIFFERENT question from Convert, and it is asked of a
   * different module.
   *
   * `POST /quotes/{id}/invoices` is `requireRole(...WRITE_ROLES)` — it carries no
   * `quotes.convert` grant and there is no `quotes.bill` permission — and what it
   * produces is an INVOICE. So the question is `invoices.create`, which is what
   * the ability model already answers for that role set. Asking
   * `canConvertQuote()` here would gate one endpoint on another's permission
   * row; asking admin would hide it from the write roles that hold it.
   *
   * `viewer` is a 403 on the API, which is the one tier this denies.
   */
  const canBillLines = ability.canCreate('invoices');
  const billLinesDenial = denialReason(ability, 'create', 'invoices');
  /*
   * The grid both ADDS and EDITS rows behind one flag, and the server guards
   * both with the same write role, so `canCreate` and `canUpdate` cannot
   * disagree for quotes today. Requiring both is the conservative reading if
   * they ever do — the cost of being wrong that way is a read-only table, not a
   * button that 403s.
   */
  const canEditLines = canCreate && canUpdate;
  /*
   * Why this account cannot delete a line, in the ability model's own words.
   * The grid shows it on the disabled control rather than hiding it — create
   * and delete are different tiers here, and a control that simply vanishes
   * does not explain that asymmetry to the `advanced_user` who hits it.
   */
  const deleteDenialReason = denialReason(ability, 'delete', 'quotes');
  /*
   * Creating this quote in Xero is a WRITE on the quote — `requireRole(
   * ...WRITE_ROLES)`, exactly like convert — so the panel is gated on
   * `canUpdate`, never on admin and never on `/permissions/me`, which
   * under-reports the write roles and would hide a button that works.
   *
   * Setting the sales defaults is a write on a DIFFERENT record and asks a
   * different question — `companies.update`, which can genuinely disagree with
   * `quotes.update`. It is deliberately NOT asked here: the dialog asks it
   * itself and explains the refusal with `denialReason`, which is a better
   * answer than a button that quietly is not offered.
   */

  const loadQuote = useCallback(
    (signal?: AbortSignal) => {
      if (quoteId === null) return Promise.resolve();
      const settle = (value: LoadResult) => setResult({ id: quoteId, value });
      return api
        .getQuote(quoteId, signal)
        .then((response) => settle({ kind: 'ready', quote: response.data }))
        .catch((caught: unknown) => {
          if (signal?.aborted) return;
          if (caught instanceof ApiError && caught.status === 404) {
            settle({ kind: 'notfound' });
            return;
          }
          settle({
            kind: 'error',
            message: caught instanceof Error ? caught.message : 'Failed to load this quote',
          });
        });
    },
    [quoteId],
  );


  const loadLines = useCallback(
    (signal?: AbortSignal) => {
      if (quoteId === null) return Promise.resolve();
      // Not paginated: the whole basket arrives at once, which is what totalling
      // it and testing it for hand-entered header totals both need anyway.
      return api
        .listQuoteProducts(quoteId, signal)
        .then((response) => setLinesResult({ id: quoteId, lines: response.data, error: null }))
        .catch((caught: unknown) => {
          if (signal?.aborted) return;
          setLinesResult({
            id: quoteId,
            lines: null,
            error: caught instanceof Error ? caught.message : 'Failed to load the line items',
          });
        });
    },
    [quoteId],
  );

  const loadInvoices = useCallback(
    (signal?: AbortSignal) => {
      if (quoteId === null) return Promise.resolve();
      /*
       * A quote carries no pointer to its invoices — the link lives on the
       * invoice — so this is a filtered list, not an embed. `perPage: 100`
       * because the panel renders them all; `meta.total` is kept separately so
       * that a hypothetical 101st still disables Convert correctly.
       */
      return api
        .listInvoices({ quoteId, perPage: 100 }, signal)
        .then((response) =>
          setInvoicesResult({
            id: quoteId,
            invoices: response.data,
            total: response.meta.total,
            error: null,
          }),
        )
        .catch((caught: unknown) => {
          if (signal?.aborted) return;
          setInvoicesResult({
            id: quoteId,
            invoices: null,
            total: null,
            error: caught instanceof Error ? caught.message : 'Failed to load the invoices',
          });
        });
    },
    [quoteId],
  );

  /*
   * THE REFETCH RULE, and the one thing on this page that is not optional.
   *
   * A line create, update or delete rewrites the quote's four header totals
   * from the lines, in the same transaction — and none of the three returns the
   * new header. So the quote on screen is stale the instant any of them
   * succeeds, and re-reading only the basket leaves the headline grand total
   * showing the pre-write figure. Both go back to the server, every time.
   */
  const refetchAfterLineWrite = useCallback(async () => {
    await Promise.all([loadQuote(), loadLines()]);
  }, [loadQuote, loadLines]);

  /**
   * A status check came back SIGNED, so the quote on screen is already stale.
   *
   * ⚠️ **This is not a cosmetic refresh.** The refresh that observed the
   * signature moved the quote into its accepting stage server-side. Nobody here
   * pressed anything, and nothing else on this page knows the stage changed.
   *
   * ⚠️ **The reason the INVOICES are re-read here changed on 2026-08-29**,
   * backend release `20260829011913-5fbab06`. This used to say that reaching
   * the accepting stage RAISES AN INVOICE and pushes it to Xero as a draft, so
   * a signature could put a document in the panel with nobody watching. It
   * cannot any more: accepting moves the stage and nothing else.
   *
   * The read is KEPT rather than deleted, and it is now the weaker claim it
   * always also was — a re-baseline, not a consequence. A signing round trip is
   * a long window with this page sitting open, and an invoice raised in it from
   * anywhere (Convert, or `POST /quotes/{id}/invoices`) leaves "Invoices raised
   * from this quote" empty while Convert stays enabled, which invites a
   * conversion that is a 409. One request buys that; a missing read costs a
   * panel that is confidently wrong.
   *
   * The lines are NOT re-read: no line write happened, so the four header totals
   * have not been rewritten and the basket on screen is still the basket.
   */
  const refetchAfterSignature = useCallback(() => {
    void loadQuote();
    void loadInvoices();
  }, [loadQuote, loadInvoices]);

  useEffect(() => {
    if (quoteId === null) return;
    const controller = new AbortController();
    void loadQuote(controller.signal);
    void loadLines(controller.signal);
    void loadInvoices(controller.signal);
    return () => controller.abort();
  }, [quoteId, loadQuote, loadLines, loadInvoices, reloadToken]);

  const current = result && result.id === quoteId ? result.value : null;

  /*
   * The customer this quote bills, or null — on the three Zoho imports every
   * relationship is null, and a quote with no company is refused by Xero with
   * `XERO_QUOTE_NO_COMPANY` long before the defaults matter.
   */
  const defaultsCompanyId =
    current !== null && current.kind === 'ready' ? (current.quote.company?.id ?? null) : null;

  /*
   * The DEAL's own value, shown beside the quote's totals — and deliberately
   * NOT folded into them.
   *
   * `convert` does not carry a deal's amount onto the quote, on purpose: quote
   * totals are stored and rewritten by any line write, so a copied figure would
   * be silently discarded by the first line added — or, if the quote were
   * converted first, inherited by a real invoice and pushed to Xero. The owner
   * still wanted the number visible, so it appears as what it is: the DEAL's
   * figure, labelled and linked, never restated as this quote's own.
   *
   * `quote.deal` is only `{ id, dealName }`, so the amount needs its own read.
   * Stamped with the deal id it was issued for, so a slow answer for one deal
   * can never be painted onto another.
   */
  const linkedDealId =
    current !== null && current.kind === 'ready' ? (current.quote.deal?.id ?? null) : null;

  const [dealValue, setDealValue] = useState<{
    id: number;
    amount: string | null;
    currency: string | null;
  } | null>(null);

  useEffect(() => {
    if (linkedDealId === null) return;
    const controller = new AbortController();
    void api
      .getDeal(linkedDealId, controller.signal)
      .then((response) => {
        if (controller.signal.aborted) return;
        setDealValue({
          id: linkedDealId,
          // The deal VALUE — lines total, else the typed amount — the same
          // figure the deals list and board show. Null (neither) stays hidden.
          amount: resolveDealValue(response.data),
          currency: response.data.currency,
        });
      })
      .catch(() => {
        /*
         * Silent, deliberately. This is context beside a total, never a total
         * itself — a deal that cannot be read shows nothing rather than putting
         * an error next to the figures a reader is trying to trust.
         */
      });
    return () => controller.abort();
  }, [linkedDealId]);

  /*
   * Nothing is written here before the answer is in: the reset happens in the
   * opener, so this effect only ever sets state asynchronously. Writing the
   * "loading" state from inside the effect is what strands a dialog on a
   * skeleton when the id changes mid-load.
   */
  useEffect(() => {
    if (!defaultsOpen || defaultsCompanyId === null) return;
    const controller = new AbortController();
    void api
      .getCompany(defaultsCompanyId, controller.signal)
      .then((response) => {
        if (controller.signal.aborted) return;
        setDefaultsResult({ id: defaultsCompanyId, company: response.data, error: null });
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setDefaultsResult({
          id: defaultsCompanyId,
          company: null,
          error:
            caught instanceof Error ? caught.message : 'Failed to load the customer record',
        });
      });
    return () => controller.abort();
  }, [defaultsOpen, defaultsCompanyId, defaultsToken]);

  /*
   * The company's sales tax, for the line grid's PREVIEW of a new row — built
   * exactly as `QuoteFormModal` builds its `companySalesTax`. A created line
   * takes this figure server-side (`input.taxRate ?? companyRate`), so without
   * it the grid previewed tax at 0.00 and read "Xero default" on a row the save
   * then taxed at the company rate.
   *
   * ⚠️ **READ AND NEVER SENT, and never written into a draft.** The grid only
   * previews it; the server owns the figure.
   *
   * ⚠️ **Tagged with the company it was read for**, so a slow answer for one
   * company is never shown against another, and **a failed read is simply
   * absent** — which the memo below turns into `{ known: false }`, never a
   * fabricated `0`. Deliberately NOT the Sales-defaults dialog's read: that one
   * runs only while the dialog is open.
   */
  const [companyTax, setCompanyTax] = useState<{
    companyId: number;
    rate: string | null;
    taxType: string | null;
  } | null>(null);

  useEffect(() => {
    if (defaultsCompanyId === null) return;
    const controller = new AbortController();
    void api
      .getCompany(defaultsCompanyId, controller.signal)
      .then((response) => {
        if (controller.signal.aborted) return;
        setCompanyTax({
          companyId: defaultsCompanyId,
          rate: response.data.xeroSalesTaxRate,
          taxType: response.data.xeroSalesTaxType,
        });
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        // Unknown, not zero: the memo reads a missing tag as `known: false`.
      });
    return () => controller.abort();
  }, [defaultsCompanyId]);

  const companySalesTax = useMemo<CompanySalesTax>(
    () =>
      companyTax !== null && companyTax.companyId === defaultsCompanyId
        ? { known: true, rate: companyTax.rate, taxType: companyTax.taxType }
        : { known: false },
    [companyTax, defaultsCompanyId],
  );

  const linesFor = linesResult && linesResult.id === quoteId ? linesResult : null;
  const lines = linesFor?.lines ?? null;
  const linesError = linesFor?.error ?? null;
  const linesLoading = linesFor === null;

  const invoicesFor = invoicesResult && invoicesResult.id === quoteId ? invoicesResult : null;
  const invoices = invoicesFor?.invoices ?? null;
  const invoiceTotal = invoicesFor?.total ?? null;
  const invoicesError = invoicesFor?.error ?? null;
  const invoicesLoading = invoicesFor === null;

  /*
   * ── WHETHER CONVERT MAY RUN AT ALL, and why it is computed HERE ──────────
   *
   * TWO unrelated things block convert, and they are not variants of each other.
   *
   * The second is ANY existing invoice against this quote — not a "converted"
   * flag, because there is no such flag, and the server answers 409 for a second
   * conversion. `invoiceTotal` is null only while the list is loading or after it
   * failed; converting blind in that window would trade a disabled button for an
   * error dialog, so the unknown state blocks too.
   *
   * ⚠️ **The first is STAGED, it blocks convert PERMANENTLY, and it is checked
   * FIRST** — by `convertBlockedReason`, further down, which is where the two are
   * worded and where that precedence is argued.
   *
   * Convert is outward-facing and creates a real financial record, so a button
   * whose only possible outcome is a refusal is worse than one that is disabled
   * with a reason. `billingMode` is a required field on `Quote` and is in hand
   * here, so the refusal is preventable rather than merely reportable — and the
   * 409 branch stays anyway, in `convertRefusal` inside `InvoiceFormModal`,
   * because the record can change under a live token.
   *
   * 🔴 **HOISTED ABOVE THE EARLY RETURNS BECAUSE TWO THINGS READ IT**: the header
   * button, and the `?create=invoice` effect below that presses it. **A second
   * copy of this expression is how a URL comes to step past a refusal the button
   * beside it is already showing** — and that failure is silent, because the
   * conversion it opens looks exactly like a legitimate one.
   *
   * `quoteForConvert` is null while the quote is loading or failed, which is
   * itself a block: nothing may convert a record that is not on screen. The
   * button never renders in that state; the URL can arrive in it.
   */
  const quoteForConvert = current !== null && current.kind === 'ready' ? current.quote : null;
  const convertIsStaged = quoteForConvert?.billingMode === 'staged';
  const convertBlocked =
    quoteForConvert === null || convertIsStaged || invoiceTotal === null || invoiceTotal > 0;

  /*
   * 🔴 **"BLOCKED" AND "NOT ANSWERED YET" ARE DIFFERENT, AND ONLY THE URL CARES.**
   *
   * `convertBlocked` is `true` while the quote and its invoices are still in
   * flight, which is right for the BUTTON — a disabled control that settles a
   * moment later costs nothing. It is wrong for the parameter below, which gets
   * exactly one shot: reading a still-loading page as "blocked" would spend
   * `?create=invoice` on the first render, every time, and open nothing. **The
   * menu would look broken and there would be no error anywhere to find.**
   *
   * ⚠️ **A FAILED read is ANSWERED, not pending.** An unreadable quote or an
   * invoices request that came back an error leaves `current`/`invoicesFor`
   * populated, so this is false and the parameter is spent and stripped —
   * blocked, correctly, with `convertBlockedReason` saying which. Only a genuine
   * in-flight state holds it, so this can never wait for something that will not
   * arrive.
   *
   * A non-numeric id never loads anything, and that too is answered: the page is
   * already rendering "Quote not found", so the parameter is stripped rather than
   * left in the bar for a reload to act on.
   */
  const convertAnswerPending = quoteId !== null && (current === null || invoicesLoading);

  /*
   * ── `?create=invoice`: a quote row handing this page its Convert button ──
   *
   * The "+" menu on a quote row navigates HERE rather than converting in place,
   * because the whole conversion lives in `InvoiceFormModal` on this page and a
   * dashboard row is not a `Quote`. All this does is press the button the person
   * chose there.
   *
   * ⚠️ **CONSUMED EXACTLY ONCE, AND STRIPPED FROM THE URL — `replace`, not push,
   * so Back does not re-trigger it.** Left in place, a refresh or a Back re-opens
   * the form; that form raises a document that demands money, so a re-opened form
   * is a second real financial document a few presses away. The guard re-arms only
   * once the parameter is actually gone, which is also what makes React's
   * double-invoked effects in development harmless.
   *
   * ⚠️ **IT OPENS THE FORM; IT DOES NOT CONVERT.** Nothing here posts anything —
   * the branch only sets a boolean. A URL that converted on navigation is a URL
   * somebody can bookmark, share, or hit twice.
   *
   * 🔴 **A URL IS NOT AUTHORISATION, AND IT IS NOT A WAY PAST A REFUSAL EITHER.**
   * The condition is exactly what the header button is gated and disabled on —
   * `canConvert && !convertBlocked && !busy` — so a staged quote, one that has
   * already been billed, and one whose invoices could not be read all open
   * NOTHING. The parameter is still stripped in that case: a blocked convert is a
   * state the page keeps, not a request to retry on the next render.
   *
   * ⚠️ **Nothing is rendered to report a blocked arrival, deliberately.** The
   * disabled Convert button is already on screen carrying `convertBlockedReason`
   * as its `title`, which is the honest report; a banner beside it would be a
   * second sentence about one refusal, and the two would drift.
   */
  const createParamConsumed = useRef(false);
  useEffect(() => {
    const requested = searchParams.get('create');
    if (requested === null) {
      createParamConsumed.current = false;
      return;
    }
    if (createParamConsumed.current) return;
    /*
     * HELD, not spent — and the ref is deliberately still `false` here, so the
     * next render tries again. See `convertAnswerPending`: nothing may be
     * decided about a convert whose blocking facts are still in flight.
     */
    if (convertAnswerPending) return;
    createParamConsumed.current = true;

    // Stripped whatever the value was, including one this page does not
    // recognise: a parameter left in the bar is one a reload acts on.
    const next = new URLSearchParams(searchParams);
    next.delete('create');
    setSearchParams(next, { replace: true });

    /*
     * `react(set-state-in-effect)` fires on the branch below and its usual
     * remedy does not apply. Deriving during render works for a parameter that
     * STAYS in the URL; this one must be spent and removed, so the open is an
     * event (arriving with the parameter) rather than a function of the URL, and
     * there is no render-time expression that is true exactly once.
     */
    if (requested === 'invoice' && canConvert && !convertBlocked && !busy) {
      // oxlint-disable-next-line react/set-state-in-effect
      setPendingConvert(true);
    }
  }, [searchParams, setSearchParams, canConvert, convertBlocked, convertAnswerPending, busy]);

  /*
   * ── "Send for signature" is available ────────────────────────────────────
   *
   * Exactly the condition the header menu's Send item is enabled on
   * (`SignatureMenu`: control `send`, `canSend`, not `busy`), named once so the
   * PDF viewer and `?sign=1` cannot offer a send the header refuses. An unknown
   * envelope list is `none`, never `send`: a send over an unknown list can raise
   * a SECOND request to a real customer.
   */
  const signAvailable = signatureControl.kind === 'send' && canSend && !busy;

  function openSendForSignature() {
    // Same as the header's own Send: a stale "change signer" seed is cleared.
    setSignatureSeed(null);
    setSendingSignature(true);
  }

  /*
   * THE PDF VIEWER — one host for the page. The header button and `?preview=1`
   * both open it. `hideOpenQuote`: this IS the quote. Mark Sent and "Send to
   * Xero and preview" change the quote's stage and Xero link, so the quote is
   * re-read after either.
   */
  // Why the viewer's Send for signature is disabled when `signAvailable` is
  // false (the viewer itself already hides it without `quotes.send`).
  const signDisabledReason = signAvailable
    ? null
    : signatureControl.kind === 'outstanding'
      ? 'This quote already has a signature request out. Void it before sending another.'
      : busy
        ? 'Wait for the current change to finish.'
        : envelopeState.loading
          ? 'Signature requests for this quote are still loading.'
          : null;
  // DocuSign not wired, or its envelope list failed: the header offers no
  // Send there, so the viewer hides it rather than inventing a reason.
  const hideViewerSign =
    !signAvailable && signatureControl.kind === 'none' && !busy && !envelopeState.loading;
  const preview = useQuotePdfPreview({
    hideOpenQuote: true,
    hideSendForSignature: hideViewerSign,
    sendForSignatureDisabledReason: signDisabledReason,
    onChanged: () => {
      void loadQuote();
    },
    onSendForSignature: () => {
      if (signAvailable) openSendForSignature();
    },
  });
  const openPreview = preview.open;

  /*
   * ── `?preview=1` and `?sign=1`: links from elsewhere opening the viewer or
   * the signature dialog on arrival ──
   *
   * Same discipline as `?create=` above: HELD until the facts the decision needs
   * have arrived, then consumed exactly once and stripped with `replace`, so a
   * reload or Back does not re-open anything. The ref re-arms only once the key
   * is gone, which is what makes StrictMode's doubled effects harmless.
   *
   * 🔴 A URL is not a way past a refusal: `?sign=1` opens the dialog only when
   * `signAvailable` — the header's own gate — and is otherwise stripped and
   * dropped silently (the header menu already carries the reason).
   */
  const previewParamConsumed = useRef(false);
  useEffect(() => {
    const requested = searchParams.get('preview');
    if (requested === null) {
      previewParamConsumed.current = false;
      return;
    }
    if (previewParamConsumed.current) return;
    // Held while the quote is still loading.
    if (quoteId !== null && current === null) return;
    previewParamConsumed.current = true;

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('preview');
        return next;
      },
      { replace: true },
    );

    if (requested === '1' && canReadModule && current !== null && current.kind === 'ready') {
      const { quote: arrived } = current;
      const target: QuotePdfPreviewTarget = {
        id: arrived.id,
        quoteNumber: arrived.quoteNumber,
        subject: arrived.subject,
        companyName: arrived.company ? documentCustomerName(arrived.company) : null,
      };
      openPreview(target);
    }
  }, [searchParams, setSearchParams, quoteId, current, canReadModule, openPreview]);

  const signParamConsumed = useRef(false);
  useEffect(() => {
    const requested = searchParams.get('sign');
    if (requested === null) {
      signParamConsumed.current = false;
      return;
    }
    if (signParamConsumed.current) return;
    // Held while the quote or its envelopes are still loading: an unknown
    // envelope list is not "no envelopes".
    if (quoteId !== null && (current === null || envelopeState.loading)) return;
    signParamConsumed.current = true;

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('sign');
        return next;
      },
      { replace: true },
    );

    if (requested === '1' && canReadModule && current !== null && current.kind === 'ready' && signAvailable) {
      // oxlint-disable-next-line react/set-state-in-effect
      setSignatureSeed(null);
      setSendingSignature(true);
    }
  }, [
    searchParams,
    setSearchParams,
    quoteId,
    current,
    envelopeState.loading,
    canReadModule,
    signAvailable,
  ]);

  /* Same id stamp as every other slot on this page: a result for a different
     company can never be read as this one's. */
  const defaultsFor =
    defaultsResult && defaultsResult.id === defaultsCompanyId ? defaultsResult : null;
  const defaultsCompany = defaultsFor?.company ?? null;
  const defaultsError = defaultsFor?.error ?? null;
  const defaultsLoading = defaultsOpen && defaultsCompanyId !== null && defaultsFor === null;

  function openXeroDefaults() {
    /*
     * Cleared here rather than in the effect, so the effect only ever writes
     * state asynchronously — and so a previous attempt's failure, or the
     * previous save's message, never greets the next one.
     */
    setDefaultsResult(null);
    setDefaultsSaved(null);
    setDefaultsOpen(true);
  }

  function closeXeroDefaults() {
    setDefaultsOpen(false);
  }

  /*
   * The two figures the delete confirmation names, fetched together when it
   * opens. Either failing leaves both unknown, which `attachmentsSentence`
   * says plainly rather than half-reporting.
   *
   * The task figure is the LIST total, deliberately not `taskSummary`'s:
   * summary counts only what is still OPEN, and a closed task is destroyed by
   * this delete just the same. `perPage: 1` because only `meta.total` is read.
   *
   * ⚠️ **`systemCount` is typed as required and can still be absent at
   * RUNTIME** — a server predating it serves the row without the key. The
   * `typeof` test is what keeps that out of the sentence: an absent figure
   * reports NO automatic notes, so the dialog reads exactly as it did before
   * this field existed rather than printing `undefined`. ⛔ It is NOT a `?? 0`
   * over a value the server sent: a real `0` and an absent key are both "say
   * nothing about automatic notes" here, and only the absent one is a guess.
   */
  function loadAttachmentTotals() {
    if (quoteId === null) return;
    setAttachmentTotals({ tasks: null, notes: null, systemNotes: 0 });
    void Promise.all([
      api.listTasks({ quoteId, perPage: 1 }),
      api.noteSummary({ target: 'quote', ids: String(quoteId) }),
    ])
      .then(([taskPage, notes]) => {
        const summary = notes.data[0] ?? null;
        setAttachmentTotals({
          tasks: taskPage.meta.total,
          notes: summary?.count ?? 0,
          systemNotes:
            summary !== null && typeof summary.systemCount === 'number' ? summary.systemCount : 0,
        });
      })
      .catch(() => setAttachmentTotals({ tasks: null, notes: null, systemNotes: 0 }));
  }

  /*
   * Deleting a LINE is `QuoteLineItemsTable`'s job now, confirmation and 403
   * handling included — it owns the row, so it is the only surface that knows
   * which line is meant, and a second delete path on this page could only
   * disagree with it. `onLinesChanged` brings the header totals back here.
   */

  async function removeQuote() {
    if (quoteId === null) return;
    setBusy(true);
    setActionError(null);
    try {
      await api.deleteQuote(quoteId);
      navigate('/quotes', { replace: true });
    } catch (caught) {
      /*
       * A 409 means DocuSign. `f_docusign_envelopes.quote_id` is ON DELETE
       * RESTRICT: an envelope — and any signature already on it — exists inside
       * DocuSign whether or not our row does, so the server refuses rather than
       * orphaning a legal record with nothing left to say what it settles.
       *
       * Surfaced verbatim, with the dialog left open, exactly as the convert
       * 409 is: the message names the quote, the envelope count and whether the
       * envelopes are still out for signature, and paraphrasing it loses the one
       * thing the reader needs in order to act.
       */
      if (caught instanceof ApiError && caught.status === 409) {
        setActionError(caught.message);
      } else if (caught instanceof ApiError && caught.status === 403) {
        /*
         * The role check runs before the row lookup, so a 403 says nothing about
         * whether the quote still exists — and a role can change mid-session
         * even though the button is gated on it.
         */
        setActionError(
          denialReason(ability, 'delete', 'quotes') ??
            'Your access changed — the quote was not deleted.',
        );
      } else if (caught instanceof ApiError && caught.status === 404) {
        setActionError('This quote has already been deleted.');
      } else {
        setActionError(caught instanceof Error ? caught.message : 'Could not delete the quote');
      }
      setBusy(false);
    }
  }

  /*
   * ── CONVERT, and what this page no longer does ───────────────────────────
   *
   * ⚠️ **The request used to be fired from here, from a ConfirmDialog, and it
   * is not any more.** On the owner's instruction Convert opens a prefilled
   * Create-Invoice modal and nothing is created until Save. `InvoiceFormModal`
   * owns the whole conversion from that point: the `POST /quotes/{id}/convert`,
   * the follow-up `PATCH /invoices/{newId}` carrying whatever was edited, both
   * 409 branches — staged and already-billed, which mean opposite things — and
   * the report for the case where the convert lands and the PATCH does not.
   *
   * ⚠️ **Do not reinstate a `convertToInvoice` here.** Two implementations of
   * one conversion is how a list page and a detail page came to report the same
   * 409 differently once already, and the modal is now the single copy for both.
   *
   * `POST /quotes/{id}/convert` RESOLVES TO AN INVOICE, never a quote — the two
   * differ in exactly the fields this page reads (`billDate`/`dueDate` against
   * `issueDate`/`validTill`, `status` against `stage`) — so the record handed
   * back is navigated to rather than fed into any state here.
   */
  function afterConvert(invoice: Invoice) {
    navigate(`/invoices/${invoice.id}`);
  }

  /*
   * The read gate, below every hook so the early return cannot change the hook
   * order — and ABOVE the not-found and error branches, deliberately. A reader
   * who may not see quotes must not be told whether this one exists: "Quote not
   * found" over a record that is merely hidden from them is a wrong answer
   * dressed as a real one.
   *
   * ⚠️ **A detail page needs the same gate as its list.** `/quotes/:id` open
   * while `/quotes` refuses is the same hole one level down, and a detail page
   * is exactly what a stale bookmark or a link in an email points at.
   *
   * ⚠️ DEGRADES OPEN: only an explicit `none` scope denies. And **hiding a page
   * is not refusing a request** — `GET /quotes/:id` answers the same to
   * everybody, so nothing here may be worded as though the record were
   * protected. This is a view control.
   *
   * No back link: `/quotes` answers this reader with the same refusal.
   */
  if (!canReadModule) {
    return (
      <>
        <PageHeader eyebrow="Operations" title="Quotes" />
        <Panel className="min-w-0">
          <EmptyState
            title="This page isn’t available to your account"
            description="Your permission set does not include this. An administrator can change that."
          />
        </Panel>
      </>
    );
  }

  if (quoteId === null) {
    return (
      <>
        <PageHeader eyebrow={<BackToQuotes />} title="Quote not found" />
        <Panel>
          <EmptyState
            icon={<QuoteIcon />}
            title="That isn’t a quote reference"
            description="A quote id is a positive whole number."
            action={
              <Button variant="secondary" size="sm" onClick={() => navigate('/quotes')}>
                Back to quotes
              </Button>
            }
          />
        </Panel>
      </>
    );
  }

  if (current === null) {
    return (
      <>
        <PageHeader eyebrow={<BackToQuotes />} title="Quote" />
        <DetailsSkeleton />
      </>
    );
  }

  if (current.kind === 'notfound') {
    return (
      <>
        <PageHeader eyebrow={<BackToQuotes />} title="Quote not found" />
        <Panel>
          <EmptyState
            icon={<QuoteIcon />}
            title="This quote doesn’t exist"
            description="It may have been deleted since this link was made."
            action={
              <Button variant="secondary" size="sm" onClick={() => navigate('/quotes')}>
                Back to quotes
              </Button>
            }
          />
        </Panel>
      </>
    );
  }

  if (current.kind === 'error') {
    return (
      <>
        <PageHeader eyebrow={<BackToQuotes />} title="Quote" />
        <Panel>
          <div
            role="alert"
            className="flex flex-col items-start gap-2 border-b border-danger-border bg-danger-soft px-4 py-3 text-xs text-danger"
          >
            <span className="font-semibold">Couldn’t load this quote.</span>
            <span className="min-w-0 [overflow-wrap:anywhere]">{current.message}</span>
          </div>
          <PanelBody>
            <Button variant="secondary" size="sm" onClick={() => setReloadToken((n) => n + 1)}>
              Try again
            </Button>
          </PanelBody>
        </Panel>
      </>
    );
  }

  const { quote } = current;

  /*
   * `quote` is the target TYPE (singular); `quoteId` is the list FILTER key.
   * The drawer derives the second from the first, so only the singular goes in
   * — and `quote.id` is used rather than the narrowed route param, since it is
   * the id the server actually answered with.
   */
  const activityTarget = { type: 'quote', id: quote.id } as const;
  /* The sheet's subheading is the only thing saying WHICH record it is about,
     so a blank subject falls back to the quote reference, then the id. */
  const activityLabel = quote.subject.trim() || quote.quoteNumber || `Quote #${quote.id}`;

  const lineCount = lines?.length ?? 0;
  const invoiceCount = invoiceTotal ?? 0;

  /*
   * THE HAND-ENTERED-TOTALS TRAP.
   *
   * The invoices have this the other way round: seven Zoho imports arrived with
   * real money and no lines. The three imported QUOTES carry no money at all —
   * so what this catches is not an import but a quote whose header figures a
   * person typed in, because the offer was agreed before anything was itemised.
   *
   * The consequence is identical, and just as invisible: posting a single line
   * makes the server rewrite all four header totals from that one line, in the
   * same transaction — no confirmation, no 409, no undo, and the typed figures
   * are not kept anywhere. So the page says it twice: a standing warning for as
   * long as the state holds, and a hard confirmation naming all four figures at
   * the moment somebody is about to destroy them.
   *
   * It is only knowable once the basket has loaded, so while the lines are
   * unknown adding one is held back rather than guessed at — a wrong guess here
   * costs the only copy of the numbers.
   */
  const handEnteredTotals = lines !== null && hasHandEnteredTotals(quote, lines);

  /*
   * The "lines total" cross-check moved into `QuoteLineItemsTable`, which sums
   * the SERVER's own `lineTotal` values through `sumDecimalStrings` exactly as
   * this page used to. It has to live there now: the grid holds unsaved rows,
   * and a total computed up here from the page's older copy of the basket would
   * disagree with the rows on screen for as long as anything was dirty.
   */

  /*
   * `modifiedBy` is stamped with the creator at INSERT, so its presence says
   * nothing about whether anyone has edited the quote. The timestamps are the
   * only honest signal.
   */
  const edited = quote.updatedAt !== quote.createdAt;
  /*
   * Expiry is no longer computed here. `ExpiryBadge` in the page header owns
   * it — one reading of validTill against the viewer's today, rather than a
   * badge and a sentence that can disagree.
   */

  /*
   * WHERE THIS QUOTE STANDS WITH XERO — the link ENVELOPE, and nothing derived.
   *
   * ⚠️ **This used to synthesise a link out of `quote.xeroQuoteId`**, on the
   * stated grounds that it was "the fact the record DOES carry". It is not a
   * fact about Xero at all: `d_quotes.xero_quote_id` is writable and **written
   * by nothing** — verified against the backend's own source — so it is `null`
   * on a quote that pushed successfully exactly as it is on one that has never
   * been near Xero. The `!== null` branch had therefore never once fired, and
   * every quote in Xero was still being offered "Create in Xero", which creates
   * a SECOND document that only a person with Xero access can delete. (That
   * button is gone from quotes and invoices as of 2026-08-30 — the save creates
   * now — but the link is still read from the envelope for every other reason,
   * and `xeroQuoteId` is still evidence of nothing.)
   *
   * That synthesis was deliberately DELETED rather than ported, as the comment
   * that stood here and CLAUDE.md both said it should be: it could express one
   * of the six statuses and had no way to say `failed`, `conflict` or
   * `archived` — the three that need a human.
   *
   * **The envelope arrives.** `quotes.repository.ts`'s `toQuote(row, bundle,
   * xero)` takes it as a REQUIRED argument, and `quotes.service.ts` fills it
   * from `envelopeFor('quotes', id)` on the single read and `envelopesFor(...)`
   * on the list — so `xero` is present on every quote row, `UNLINKED` rather
   * than absent when there is no link. `XeroEnvelope` there is field for field
   * `XeroLink` here. (`docs/api/xero.md` still says "there is no `xero` key on a
   * quote read"; that line is stale and the source wins, which is the rule this
   * repo already follows for that doc.)
   *
   * `linkOf()` supplies `NO_LINK` for a response that predates the release, so
   * an old cached read degrades to "no counterpart" rather than crashing.
   */
  const xeroLink: XeroLink = linkOf(quote);

  /**
   * Whether a `skipped` report is worth putting on screen at all.
   *
   * ⚠️ **A skip on a quote that is NOT IN XERO is the answer to every save of
   * every quote nobody has sent yet** — which is most quotes, most of the time.
   * Drawing anything for it would put a Xero notice on the whole system, and
   * the panel below already says the quote has no counterpart, in the place
   * that owns that fact.
   *
   * On a LINKED quote a skip means something else entirely and is worth a quiet
   * line: the server had a document to update and declined to send anything —
   * because the quote has no lines left, say. That is rare, and it is the one
   * skip a person can act on.
   *
   * ⚠️ **The split is read off the LINK, never off the reason's wording.** The
   * server's sentence is safe to show and is shown; which skip it describes is
   * not something to match text for, and the link answers it from data already
   * in hand.
   */
  const skipWorthShowing = xeroPush?.kind !== 'skipped' || xeroLink.id !== null;

  /*
   * The base-currency reading, and why `connected` is asked as well as
   * `baseCurrency`.
   *
   * `baseCurrency` belongs to the TENANT, not to us — `GET /xero/connection`
   * reports it and `XeroProvider` reads it once, app-wide. Requiring `connected`
   * too means a stale reading left over from a connection that has since
   * dropped can never be presented as this organisation's base currency. Both
   * checks fail the same way, into `kind: 'none'`, which renders nothing at all.
   */
  const baseView = baseCurrencyView(
    quote,
    xeroConnection.connected ? xeroConnection.baseCurrency : null,
  );
  /* Null only under `kind: 'none'`, where the row is not rendered at all. Spread
     rather than passed as `undefined`, which `exactOptionalPropertyTypes`
     refuses — the same pattern the Xero reference fields below already use. */
  const baseRateHint = rateHint(baseView);

  /*
   * Has this quote's total moved since the copy the customer holds was
   * produced? `null` on every quote that has never been pushed to Xero, and
   * `null` again the moment it is re-pushed — both correct, and both look
   * exactly like the feature not working. See `src/lib/reprice.ts`.
   *
   * ⚠️ Read straight off the record and nowhere else. In particular the amount
   * carries `repricedFromCurrency`, NOT `quote.currency`: the currency is an
   * ordinary editable column and can have been changed since the stamp, which
   * would print the old figure under the new code.
   */
  const repriced = repriceNotice(quote);

  /*
   * WHICH of the two blocks is in force, in words.
   *
   * ⚠️ **`convertIsStaged` and `convertBlocked` are declared far ABOVE this**,
   * beside the invoices they read — they have to be, because the
   * `?create=invoice` effect is a hook and this line is past the early returns.
   * The reasoning for both lives there; only the wording lives here, because it
   * needs `invoiceCount`, which needs the loaded quote.
   *
   * STAGED takes precedence over the already-billed reason on purpose. On a
   * staged quote, existing invoices are the NORMAL result of billing line by
   * line, not evidence that a conversion has been spent — so "an invoice already
   * exists, so converting is a 409" would name the wrong cause, imply something
   * was used up, and suggest the refusal would lift if those invoices went away.
   * It would not. This quote is never converted, whatever has or has not been
   * billed.
   */
  const convertBlockedReason = convertIsStaged
    ? STAGED_CONVERT_REASON
    : invoiceTotal === null
      ? invoicesError !== null
        ? 'Invoices couldn’t be loaded, so converting isn’t safe yet.'
        : 'Checking whether this quote has already been billed…'
      : /*
         * "Converting AGAIN" was the old wording and it asserts a previous
         * conversion. The sentence names the invoice rather than the act that
         * produced it — someone who never converted anything should not be told
         * they did, and `POST /quotes/{id}/invoices` reaches this panel too.
         *
         * ⚠️ **Corrected 2026-08-29**, backend release
         * `20260829011913-5fbab06`. It used to offer two origins: "from a
         * conversion, or from the quote having been accepted". Accepting a quote
         * now raises nothing, so that second clause named a route that no longer
         * exists. The guard itself is unchanged — ANY invoice carrying this
         * `quote_id` makes convert a 409, whichever endpoint raised it, which is
         * why the sentence still refuses to name a cause.
         */
        `${pluralise(invoiceCount, 'invoice')} already ${
          invoiceCount === 1 ? 'exists' : 'exist'
        } for this quote, so converting is a 409.`;

  /*
   * Whether the editable grid may be mounted at all.
   *
   * Three things hold it back, and each for a different reason:
   *
   *  - the basket is still LOADING. `handEnteredTotals` cannot be computed
   *    without it, and mounting an editable grid before the check has run is
   *    exactly the window in which one click destroys the only copy of four
   *    typed figures.
   *  - the basket FAILED to load. Same reason, and it does not resolve itself —
   *    so the grid is still mounted, but read-only, with the failure named.
   *  - the totals were hand-entered and nobody has acknowledged it yet.
   */
  const linesGate: 'loading' | 'unchecked' | 'confirm' | 'open' = linesLoading
    ? 'loading'
    : linesError !== null
      ? 'unchecked'
      : handEnteredTotals && !totalsAcknowledged
        ? 'confirm'
        : 'open';

  return (
    <>
      <PageHeader
        eyebrow={<BackToQuotes />}
        title={documentDisplayTitle(quote.subject, quote.xeroQuoteNumber)}
        description={
          <>
            <StageBadge stage={quote.stage} />
            <ExpiryBadge validTill={quote.validTill} />
            <ImportedFromXeroBadge importedAt={quote.importedFromXeroAt} />
            {quote.quoteNumber && (
              <>
                <span className="text-fg-subtle">·</span>
                <span className="tabular">{quote.quoteNumber}</span>
              </>
            )}
            {quote.company && (
              <>
                <span className="text-fg-subtle">·</span>
                <LinkedValue to={`/companies/${quote.company.id}`} name={documentCustomerName(quote.company)} />
              </>
            )}
            {quote.validTill && (
              <>
                <span className="text-fg-subtle">·</span>
                <span>Valid till {formatDateOnly(quote.validTill)}</span>
              </>
            )}
          </>
        }
        actions={
          /*
            Tasks and notes lead, and are never gated: the task and note routers
            carry no role guard, so a `viewer` who can open this quote can write
            against it. The header wraps, so the row folds at 320px rather than
            overflowing.
          */
          <>
            <ActivityButton target={activityTarget} onOpen={() => setActivityOpen(true)} />
            {/*
              THE SIGNATURE ACTIONS, ADDITIONALLY — `EnvelopePanel` keeps every
              control it has. ONE control here, opening all three, which is what
              the 320px arithmetic demanded; see `SignatureMenu` for the layout
              reasoning and for why every item is disabled-with-a-reason rather
              than hidden.
            */}
            <SignatureMenu
              control={signatureControl}
              quoteName={quote.subject.trim() || `quote #${quote.id}`}
              /*
               * `ability.canUpdate('quotes')` and its true reason — the same
               * pair `EnvelopePanel` below is handed, named once on this page.
               * Never `user.role`, never `/permissions/me`.
               */
              canManage={canSend}
              manageDenial={manageDenial}
              hasCompany={quote.company !== null}
              busy={busy}
              /*
               * Straight to the page's EXISTING state and dialogs — the same
               * `setSendingSignature` the panel's own `onSend` calls. No second
               * copy of anything: two states for one dialog is what turns a
               * second mount point into a double fire.
               */
              onSend={() => {
                /*
                 * Cleared, not left standing: a seed survives a "change signer"
                 * abandoned before its send, and an ordinary Send must open on
                 * the company's primary contact rather than on somebody a
                 * cancelled operation had chosen.
                 */
                setSignatureSeed(null);
                setSendingSignature(true);
              }}
              onChangeSigner={setChangingSignerFor}
              /*
               * The list is re-read rather than patched: this page holds no copy
               * of the envelopes to replace — the header and the panel both read
               * through `envelopeReloadKey`, so bumping it moves the two
               * together.
               */
              onReminded={() => setEnvelopeReloadKey((key) => key + 1)}
            />
            {/*
              PREVIEW, at every stage: it needs only `quotes.read` (the button
              hides itself otherwise) and opens the page's one viewer, which
              handles a quote that is not in Xero yet itself.
            */}
            <PreviewQuotePdfButton
              variant="detail"
              target={{
                id: quote.id,
                quoteNumber: quote.quoteNumber,
                subject: quote.subject,
                companyName: quote.company ? documentCustomerName(quote.company) : null,
              }}
              onPreview={preview.open}
            />
            {/*
              Gated inside the button on `ability.canExport('quotes')` (and its
              Mark Sent choice on `canSend('quotes')`), never on `user.role`. It
              renders NOTHING until the quote is in Xero — the control is hidden
              rather than disabled, and `canDownloadPdf` reads the LINK ROW,
              never `xeroQuoteNumber`.
            */}
            <DownloadPdfButton
              kind="quote"
              document={quote}
              reference={quote.subject}
              variant="detail"
            />
            {canConvert && (
              <Button
                variant="primary"
                disabled={convertBlocked || busy}
                {...(convertBlocked ? { title: convertBlockedReason } : {})}
                onClick={() => setPendingConvert(true)}
              >
                Convert to invoice
              </Button>
            )}
            {canUpdate && (
              <Button
                variant="secondary"
                leadingIcon={<PencilIcon />}
                /*
                 * The previous save's Xero report is cleared on the way in, not
                 * on the way out. Left standing it would sit under the panel
                 * describing a push that happened before whatever is about to
                 * be typed — and "Xero's copy has been sent and takes no more
                 * changes" reads as a statement about the edit in progress.
                 */
                onClick={() => {
                  setXeroPush(null);
                  setEditing(true);
                }}
              >
                Edit quote
              </Button>
            )}
            {canDelete && (
              <Button
                variant="danger"
                iconOnly
                leadingIcon={<TrashIcon />}
                aria-label={`Delete ${quote.subject}`}
                title={`Delete ${quote.subject}`}
                onClick={() => {
                  setActionError(null);
                  loadAttachmentTotals();
                  setPendingDelete(true);
                }}
              />
            )}
            {/* Icon-only: this header's row is already sized against 320px. */}
            <LayoutToolbar layout={layout} compact />
          </>
        }
      />

      {/*
        * `min-w-0` on every panel in this grid is load-bearing, not decoration.
        * A grid item defaults to min-width:auto, so the basket's wide table
        * widens the whole track instead of scrolling inside its own TableWrap,
        * and the page overflows sideways below 768px. That exact bug shipped on
        * DealDetailPage once already.
        */}
      <div className="grid gap-4">
        {/*
          The standing half of the totals guard: it stays on the page for as long
          as the state holds, so nobody has to reach the confirmation to find out
          what adding a line will cost.
        */}
        {handEnteredTotals && (
          <Panel className="min-w-0 border-warning-border bg-warning-soft">
            <PanelBody>
              <div className="flex items-start gap-2 text-warning">
                <AlertIcon />
                <div className="min-w-0 text-xs">
                  <p className="m-0 font-semibold">These totals were entered by hand.</p>
                  <p className="m-0 mt-1 [overflow-wrap:anywhere]">
                    Adding a line item replaces all four with figures from the lines.
                  </p>
                </div>
              </div>
            </PanelBody>
          </Panel>
        )}

        {/*
          The movable sections. The totals warning above stays fixed; the
          Xero notes below travel with the Xero panel they describe.
        */}
        <LayoutGrid
          layout={layout}
          render={{
            details: () => (
        <Panel className="min-w-0" aria-labelledby="quote-details-heading">
          {/* No description: the footer already carries the created stamp, with
              the creator's name on it. */}
          <PanelHeader id="quote-details-heading" title="Quote details" />

          {/*
            * The inline bank, arranged as `CompanyDetailPage` arranges its own:
            * the editable fields lead, and what this page still owns follows.
            *
            * Keyed on the record, so opening a different quote cannot carry a
            * half-typed draft or a "Saved" line across to a quote it was never
            * about. A refresh of the SAME quote — a Xero action, a line write —
            * keeps the key and therefore keeps whatever the user is mid-way
            * through typing.
            *
            * It owns subject, quote number, customer PO number, issue date,
            * valid till, currency, tax treatment, both tracking overrides and
            * terms, so those are no longer repeated as read-only rows below.
            * **Stage is deliberately NOT one of them**: it is rendered read-only
            * inside the component, and the stage is set from the Edit modal or by
            * moving the card on the quotes board. ⚠️ The REASON stated here used
            * to be that accepting raises an invoice and pushes it to Xero behind
            * an acknowledgement a blur-to-save control cannot carry; backend
            * release `20260829011913-5fbab06` (2026-08-29) removed that, so the
            * exclusion now stands on nobody having asked for the control rather
            * than on a consequence. See `InlineQuoteFields`, which owns the
            * decision.
            *
            * The Edit modal STAYS. Inline is the fast path; the modal is still
            * better for filling in many fields at once.
            *
            * ⚠️ **`onSaved` re-reads the quote AND the invoices** — the same pair
            * the Edit modal uses. This section sends no key that can raise an
            * invoice, so the second read is a cheap re-baseline across a save the
            * user was looking at, not a consequence of it: an invoice raised
            * elsewhere in the meantime leaves the panel empty while Convert stays
            * enabled, which invites a conversion that is a 409.
            *
            * ⚠️ **CORRECTED 2026-08-30. AN INLINE FIELD SAVE NOW REACHES XERO,
            * AND NOTHING ON THIS PAGE DOES IT.** This block read:
            *
            * > *"Nothing here pushes to Xero, and that is deliberate. Unlike the
            * > invoice page there is no debounce on this one: `pushQuoteToXero`
            * > fires once, synchronously, from a Save a person pressed. Calling
            * > it per inline field would rewrite a live accounting document once
            * > per field against a tenant limit of 60 Xero calls a minute shared
            * > by the whole organisation — and Xero re-numbers a quote on an
            * > update. Sending an inline edit onward is done from the Xero panel
            * > below. Lifting the invoice page's coalescing window into a shared
            * > hook is the real fix and is follow-up work, not this change."*
            *
            * The PATCH this component makes pushes on the server, so an inline
            * field edit goes to Xero on its own. The cost that paragraph was
            * protecting against is real and is now the server's to bear — it
            * takes a per-document lock, so saves serialise rather than race,
            * and rapid editing is slower rather than divergent.
            *
            * ⚠️ **CLOSED 2026-08-30. The report is wired.** This paragraph read:
            *
            * > *"⚠️ **What is missing is the REPORT, not the push.**
            * > `InlineQuoteFields` holds the response and does not carry
            * > `xeroPush` up, so this is the one save on this page that reaches
            * > Xero and says nothing. Wiring it means a prop on that component,
            * > which is not this pass's file."*
            *
            * That prop exists now — `onXeroPush`, the same name, shape and
            * contract `QuoteFormModal` takes — and it reports into the same
            * strip below. No save on this page reaches Xero silently any more.
            */}
          {/*
            ⚠️ **`PanelBody`, not a bare `div`.** This was a plain
            `<div className="min-w-0">` on all three detail pages, so the fields
            sat flush against the panel's edge while the `PanelHeader` above
            them carried `px-4 py-3` — the labels were outdented from the very
            heading that names them, and the warning at the foot ran edge to
            edge. `PanelBody`'s default density IS that same `px-4 py-3`, so the
            two line up by construction rather than by two numbers agreeing.

            `min-w-0` still has to be here: a grid item defaults to
            `min-width: auto`, which is what lets a wide value push the whole
            column out below `sm`.
          */}
          <PanelBody key={quote.id} className="min-w-0">
            <InlineQuoteFields
              quote={quote}
              onSaved={() => {
                void loadQuote();
                void loadInvoices();
              }}
              /*
               * ⚠️ The same one-line classification the Edit modal uses, into
               * the same state. Wording it here instead would be a second
               * reader of one wire shape on one page, which is how the two come
               * to describe the same refusal differently.
               */
              onXeroPush={(report) => setXeroPush(autoPushOutcomeOf(report))}
            />
          </PanelBody>

          {/*
            ⚠️ **KEPT even though the section above prints the same four totals**,
            and the duplication is the lesser of two faults rather than an
            oversight. This group renders them through `InvoiceMoney`, so every
            figure carries its currency code — and money on this API is
            per-document and never converted, so a bare `17500.00` is a figure
            whose currency the reader has to guess. It also carries three things
            the section above cannot show at all: the DEAL's own value, the grand
            total in the Xero organisation's base currency, and the exchange rate
            that reconciles them.

            Dropping it to remove the repetition would trade a cosmetic problem
            for a correctness one, on the figures a customer is invoiced against.
            The real fix is a prop on the inline section suppressing its own
            totals; that belongs to the component, not to this page.
          */}
          <DetailGroup title="Totals">
            <DetailItem label="Sub total">
              <InvoiceMoney value={quote.subTotal} currency={quote.currency} />
            </DetailItem>
            {/*
              discountTotal and taxTotal are nullable and are null on every
              imported quote. Null means "nobody ever said", which is a different
              fact from a recorded zero — so it is an em dash, never `?? '0.00'`.
            */}
            <DetailItem label="Discount total">
              <InvoiceMoney
                value={quote.discountTotal}
                currency={quote.currency}
                emptyLabel="Not recorded"
              />
            </DetailItem>
            <DetailItem label="Tax total">
              <InvoiceMoney
                value={quote.taxTotal}
                currency={quote.currency}
                emptyLabel="Not recorded"
              />
            </DetailItem>
            {/*
              The headline, and deliberately the STORED column rather than a sum
              of the lines. It is what `convert` copies onto the invoice, and on
              a quote with no lines it is the only figure that exists at all.
            */}
            {/*
              The deal's figure, when this quote came from one. Labelled as the
              DEAL's value and linked, so it can never be read as a total of
              these lines — which is precisely what it is not. The id check
              means a stale answer for a previous deal renders nothing.
            */}
            {dealValue !== null &&
              dealValue.id === linkedDealId &&
              dealValue.amount !== null && (
                <DetailItem label="Deal value">
                  <span className="text-fg-secondary">
                    <InvoiceMoney value={dealValue.amount} currency={dealValue.currency} />
                  </span>{' '}
                  <Link
                    to={`/deals/${dealValue.id}`}
                    className="text-sm text-accent-brand underline"
                  >
                    on the deal
                  </Link>
                </DetailItem>
              )}
            <DetailItem label="Grand total">
              <span className="text-base font-semibold text-fg">
                <InvoiceMoney value={quote.grandTotal} currency={quote.currency} />
              </span>
              {/*
                The same total in the Xero organisation's own currency, and
                deliberately SECONDARY to the one above it: the document
                currency is what the customer is billed in, so it stays the
                headline and this sits under it, smaller and muted — the
                arrangement Xero itself uses.

                ⚠️ Rendered in the `converted` branch and nowhere else. The
                other four have no figure to render, and printing an
                approximation where there is none is the single thing this whole
                block exists not to do.

                `InvoiceMoney` rather than hand-formatting, so the two figures
                are separated by nothing but weight and colour — a converted
                total that read differently from the total above it would look
                like a different KIND of number rather than the same number in
                another currency.
              */}
              {baseView.kind === 'converted' && (
                <span className="mt-0.5 block text-sm font-normal text-fg-muted">
                  <InvoiceMoney
                    value={baseView.amount}
                    currency={baseView.rate.baseCurrency}
                  />
                </span>
              )}
            </DetailItem>
            {/*
              THE RATE LINE — Xero's own third row, and the only thing that
              reconciles the two figures above.

              It sits in the Totals group rather than beside "Currency" further
              down because adjacency is what makes the second figure legible: a
              rate a screen away from the number it produced explains nothing.

              Absent entirely under `kind: 'none'`. That covers both a quote
              already in the base currency and a disconnected Xero, and in
              neither case is there anything true to say — a row reading "not
              applicable" would be a row about the integration rather than about
              this quote.
            */}
            {baseView.kind !== 'none' && (
              <DetailItem
                label="Exchange rate"
                {...(baseRateHint === null ? {} : { hint: baseRateHint })}
              >
                {baseView.kind === 'no-rate' ? (
                  <span className="text-fg-subtle">Not recorded</span>
                ) : baseView.kind === 'unusable-rate' ? (
                  /*
                    ⚠️ The stored value VERBATIM, and deliberately NOT through
                    `formatRateLine`. That helper is documented as total only
                    over an `ExchangeRate` built by `exchangeRateOf`, which
                    refused this one — pushing it through anyway would render
                    "1 GBP = 0 AUD", a sentence that reads as a measured rate and
                    is arithmetic nonsense.

                    Shown rather than hidden because the value is a fact about
                    the record: reporting it as "Not recorded" would send someone
                    to enter a rate that is already there.
                  */
                  <span className="tabular [overflow-wrap:anywhere]">{baseView.recorded}</span>
                ) : (
                  <span className="tabular [overflow-wrap:anywhere]">
                    {formatRateLine(baseView.rate)}
                  </span>
                )}
              </DetailItem>
            )}
          </DetailGroup>

          {/*
            ⛔ **NOT helper text, and not to be removed by a copy sweep.** It
            names an irreversible consequence nothing else on this page can
            show: the figure above is not the figure the customer is holding.
            `src/lib/reprice.ts` carries the reasoning and the wording; this
            page renders it and decides nothing.

            It sits directly under the Totals group because the fact is ABOUT
            that grand total — a sentence a screen away from the number it
            qualifies is a sentence nobody joins up.

            ⚠️ **`info`, not `warning`.** Nothing has gone wrong and the quote
            is correct; what is stale is the copy somebody was sent. A warning
            tone would send the reader hunting a fault in this record. The
            headline sentence is the signal, so the tone is never doing the work
            alone.

            ⚠️ **No re-push control here, deliberately.** Re-pushing rewrites a
            live accounting document and re-numbers the quote in Xero, so it is
            not a button offered beside a statement of fact.

            ⚠️ **CORRECTED 2026-08-30 — where it DOES happen has moved.** This
            said the re-push was *"its own decision, made from the Xero panel
            below by somebody who went looking for it."* The panel offers no
            push for a quote at all now (`quotes.capabilities.sync` is false and
            the create is withheld — `CREATED_BY_SAVING`); **the server pushes
            inside every save**, so the next edit is what re-sends it. The
            reason for withholding a control HERE is unchanged.
          */}
          {repriced !== null && (
            <PanelBody className="border-b border-border last:border-b-0">
              <div className="rounded-md border border-info-border bg-info-soft px-3 py-2 text-xs text-info">
                <p className="m-0 font-semibold [overflow-wrap:anywhere]">{repriced.headline}</p>
                <p className="m-0 mt-1 [overflow-wrap:anywhere]">{repriced.body}</p>
              </div>
            </PanelBody>
          )}

          <DetailGroup title="Relationship">
            {/*
              Each `to` is built inside its own guard. Interpolating an optional
              id straight into a path gives "/companies/undefined" on the three
              imports, where every one of these embeds is null.
            */}
            <DetailItem label="Company">
              <LinkedValue
                {...(quote.company ? { to: `/companies/${quote.company.id}` } : {})}
                name={quote.company ? documentCustomerName(quote.company) : undefined}
              />
            </DetailItem>
            <DetailItem label="Contact">
              <LinkedValue
                {...(quote.contact ? { to: `/contacts/${quote.contact.id}` } : {})}
                name={quote.contact?.name}
              />
            </DetailItem>
            {/* The deal embed is keyed `dealName`, not `name`. */}
            <DetailItem label="Deal">
              <LinkedValue
                {...(quote.deal ? { to: `/deals/${quote.deal.id}` } : {})}
                name={quote.deal?.dealName}
              />
            </DetailItem>
            {/*
              The account lead, NOT the owner. `accountLead` is the RESOLVED
              value — the quote's own override where it has one, otherwise its
              company's — and it is display-only here because the owner retired
              lead-assignment on quotes in favour of the company's.

              ⚠️ **CORRECTION (2026-08-30): this said "`accountLeadId` in a
              write body is a 422, so this is display and can never become an
              editor". FALSE.** Migration 082 added `d_quotes.account_lead_id`
              and the key is on the quote write schema
              (`quotes.schema.ts:211`, `optionalUserId()`, verified from backend
              source); `null` there means DERIVE, not "no lead". It is refused
              only on a DELIVERABLE. Display-only is the product decision and it
              stands — but "can never become an editor" was never true of the
              API, and reading it that way turns a buildable change into a
              reported backend gap.

              ⚠️ This is NOT a rename of the owner field: the two are different
              people (measured live — quote 9's owner is Paras Fatnani while its
              account lead is Daryna Kodinets), so a quote read before and after
              this change shows a different name in the same place. The label
              must never imply they are the same person, and the owner's name is
              simply no longer shown here.
            */}
            <DetailItem label="Account lead">
              <StaffValue person={quote.accountLead} />
            </DetailItem>
            <CreditedTo
              creditedTo={quote.creditedTo}
              accountLeadId={quote.accountLead?.id}
              labelClassName="text-2xs font-semibold tracking-[0.06em] text-fg-subtle uppercase"
            />
          </DetailGroup>

          {/*
            The project and its cities are one idea and are grouped as one,
            because the second is a consequence of the first rather than a
            second field.
          */}
          <DetailGroup title="Project" columns="sm:grid-cols-2">
            {/*
              No hint either way. That the project crosses on conversion is
              already stated in the convert confirmation, where it is a
              consequence somebody is about to accept rather than trivia; and
              "None recorded" is what the value itself says — the page does not
              assert that there IS none, because the lookup swallows its own
              failures.
            */}
            <DetailItem label="Project">
              {quote.project ? (
                <LinkedValue to={`/projects/${quote.project.id}`} name={quote.project.name} />
              ) : (
                <span className="text-fg-subtle">None recorded</span>
              )}
            </DetailItem>
            {/*
              The ONE clause kept, hard-compressed: these cities are read live
              through the project rather than stored here, so they are not a
              snapshot and there is no control for them on this page. Without
              "edited on the project, not here" a reader hunts for an editor
              that does not exist, and reads the list as fixed at the moment the
              quote was sent when it is not.

              Each city is shown with its country: names repeat across countries,
              and the country is now reachable only through a city.
            */}
            <DetailItem label="Cities">
              {quote.cities.length === 0 ? (
                <span className="text-fg-subtle">
                  {quote.project ? 'This project lists no cities' : '—'}
                </span>
              ) : (
                <ul className="flex flex-wrap gap-1.5">
                  {quote.cities.map((city) => (
                    <li key={city.id}>
                      <Badge tone="neutral">{cityLabel(city)}</Badge>
                    </li>
                  ))}
                </ul>
              )}
            </DetailItem>
          </DetailGroup>

          {/*
            ISSUE DATE, VALID TILL, CURRENCY and STAGE have all moved into the
            inline section above — the first three as editable fields, the stage
            as a read-only row there, for the acceptance reason given at the
            mount. Nothing is lost by their absence here: expiry is still on the
            page header via `ExpiryBadge`, computed the same way, and the
            exchange rate that reconciles the two currency figures is in Totals
            beside the figure it produced.
          */}
          {/*
            THE TWO XERO-SIDE references, and only those. A quote carries four;
            the other two — `quoteNumber` and the CUSTOMER's PO number — are
            EMERGE's own and have moved into the inline section above, where they
            are editable rather than merely shown. Repeating them here would be
            one value with two renderings, and the read-only twin is always the
            half that goes stale.

            These two stay behind because neither is ours to type: one is written
            by a successful push, and the other by nothing at all.
          */}
          <DetailGroup title="References">
            {/*
              ⚠️ **NEITHER of these two answers "is this quote in Xero?", and an
              empty box must not be read as saying it does.** The explanatory
              paragraphs that used to hang under both are gone with the rest of
              the page's narration — but the WRONG CONCLUSION they existed to
              stop is not decoration, so a short clause survives on each, shown
              only in the branch where it can mislead.

              `xeroQuoteNumber` IS written by the server after a successful
              push, so a value here is real and needs no gloss. An EMPTY one
              proves nothing: the push stores the number on a best-effort basis
              and deliberately does not fail if that write is refused, because
              failing a call over a cosmetic column would send somebody looking
              for a document that exists.

              `xeroQuoteId` is written by NOTHING — the column is writable and
              no code fills it, verified against the backend's own source. It is
              `null` on a quote that pushed successfully, so its hint stands in
              BOTH branches, and the label keeps "(hand-entered)": a box that
              looks like the Xero id and is not is worse than no box, because
              somebody will copy a GUID into it.

              Both point at the Xero panel, which reads the link envelope and is
              the one place on this page that can say where the quote stands.
            */}
            <DetailItem
              label="Xero quote number"
              {...(quote.xeroQuoteNumber
                ? {}
                : { hint: 'Empty isn’t proof — see the panel below.' })}
            >
              {quote.xeroQuoteNumber ? (
                <span className="tabular">{quote.xeroQuoteNumber}</span>
              ) : (
                <span className="text-fg-subtle">Not recorded</span>
              )}
            </DetailItem>
            <DetailItem
              label="Xero ID (hand-entered)"
              hint="Not used — the link is in the panel below."
            >
              {quote.xeroQuoteId ? (
                <span className="font-mono text-xs">{quote.xeroQuoteId}</span>
              ) : (
                <span className="text-fg-subtle">Not recorded</span>
              )}
            </DetailItem>
          </DetailGroup>

          <PanelFooter>
            <p className="m-0 text-xs text-fg-muted [overflow-wrap:anywhere]">
              Created {formatDateTime(quote.createdAt)}
              {quote.createdBy ? ` by ${quote.createdBy.name}` : ''}
            </p>
            {/*
              modifiedBy is stamped with the creator at insert, so it is NOT an
              "edited by" marker and must not be labelled as one. Comparing the
              two timestamps is the only honest test.
            */}
            <p className="m-0 text-xs text-fg-muted [overflow-wrap:anywhere]">
              {edited
                ? `Last updated ${formatDateTime(quote.updatedAt)}`
                : 'Not changed since it was created'}
            </p>
          </PanelFooter>
        </Panel>
            ),
            lines: () => (
        <>
        {/*
          * THE LINE ITEMS, and the component that owns them.
          *
          * `QuoteLineItemsTable` fetches the lines, edits them in place, saves
          * row by row and confirms its own deletes. It is mounted here rather
          * than reimplemented: it already existed in this repo, fully written,
          * and was imported by nothing — the page rendered a read-only copy of
          * the same collection beside a one-row-at-a-time modal, which is two
          * surfaces over one set of data and the way the two come to disagree.
          *
          * It brings its own heading, so this Panel deliberately has no
          * PanelHeader. `min-w-0` on the Panel is still load-bearing: the grid
          * scrolls a wide row inside itself, and a grid item defaults to
          * min-width:auto, which would widen the whole track instead.
          */}
        <Panel className="min-w-0">
          <PanelBody>
            {linesGate === 'loading' ? (
              /*
               * The page's OWN basket read, not the grid's. The grid is held
               * back until this lands because `handEnteredTotals` cannot be
               * computed without it — see `linesGate`.
               */
              <div className="grid gap-2" aria-busy="true">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-9 w-full" />
                <Skeleton className="h-9 w-full" />
                <Skeleton className="h-9 w-full" />
              </div>
            ) : linesGate === 'confirm' ? (
              /*
               * The hard half of the totals guard. A banner alone is not
               * enough: the first line item is a one-way, unconfirmed, unlogged
               * overwrite of the only copy of four figures, and the grid's own
               * "Add row" button is inside a component this page does not own —
               * so the gate is in front of the grid rather than on the button.
               */
              <div className="grid gap-3">
                <div>
                  <h3 className="text-sm font-semibold text-fg">Line items</h3>
                  <p className="m-0 mt-1 text-xs text-fg-muted [overflow-wrap:anywhere]">
                    None yet. The totals above were typed by hand — the first line replaces all
                    four with figures from the lines.
                  </p>
                </div>
                {canEditLines ? (
                  <div>
                    <Button
                      variant="secondary"
                      size="sm"
                      leadingIcon={<PlusIcon />}
                      onClick={() => {
                        setActionError(null);
                        setPendingTotalsAdd(true);
                      }}
                    >
                      Add line items
                    </Button>
                  </div>
                ) : (
                  <p className="m-0 text-xs text-fg-muted">
                    {denialReason(ability, 'update', 'quotes') ??
                      'You cannot change the lines on this quote.'}
                  </p>
                )}
              </div>
            ) : (
              <>
                {linesGate === 'unchecked' && (
                  /*
                   * The page's own basket read failed, so whether these totals
                   * were hand-entered is UNKNOWN — and the grid is mounted
                   * read-only rather than hidden. Hiding it would lose the
                   * lines a person came to read over a check that is about
                   * writing; leaving it writable would risk the overwrite the
                   * check exists to prevent.
                   */
                  <div
                    role="alert"
                    className="mb-3 flex flex-wrap items-start gap-x-2 gap-y-1 rounded-md border border-danger-border bg-danger-soft px-3 py-2 text-xs text-danger"
                  >
                    <span className="font-semibold">Editing is held back.</span>
                    <span className="min-w-0 [overflow-wrap:anywhere]">
                      Couldn&rsquo;t re-read this quote&rsquo;s lines ({linesError}). They are
                      still shown, but read-only.
                    </span>
                    <Button variant="secondary" size="sm" onClick={() => void loadLines()}>
                      Try again
                    </Button>
                  </div>
                )}
                <QuoteLineItemsTable
                  /*
                   * ⚠️ **The `key` is a REMOUNT, and it is deliberate.**
                   *
                   * The grid reads its lines once, on mount. Each line carries a
                   * derived `invoiced` flag, and the grid's delete confirmation
                   * uses it for the one warning nothing else gives — the backend
                   * has no guard at all on deleting a billed quote line, so a
                   * stale "not billed" reading is a silent severing of an
                   * invoice line's provenance.
                   *
                   * CONVERTING the quote, or billing some of its lines through
                   * "Bill line items", raises an invoice from these lines and
                   * flips those flags with nothing telling the grid. Keying on
                   * the invoice count makes exactly that event re-read them, and
                   * nothing else: a line write does not move the count, so
                   * unsaved rows survive an ordinary save. `null` (still
                   * loading, or the read failed) is its own key rather than
                   * folded into 0, so arriving at a count is not mistaken for
                   * the count changing.
                   *
                   * ⚠️ **Corrected 2026-08-29**, backend release
                   * `20260829011913-5fbab06`: this named ACCEPTING the quote,
                   * from the stage field in the form above, as the event that
                   * raises an invoice from these lines. Accepting now moves the
                   * stage and raises nothing, so it was the one example given
                   * for a remount that is still needed for the other two.
                   */
                  key={`lines-${invoiceTotal ?? 'unknown'}`}
                  quoteId={quoteId}
                  /*
                   * The QUOTE's own cities — the HEADER project's set, derived
                   * on every read and already on the record, so no extra
                   * request.
                   *
                   * ⚠️ It is deliberately not a line's option list. Since
                   * migration 067 a line names its OWN project and `cityIds` is
                   * validated against that project's cities, so the component
                   * uses this only to name a city it has no other record of and
                   * to describe what the header covers. Never `/cities` here:
                   * the master offers rows every line write refuses.
                   */
                  cities={quote.cities}
                  /*
                   * ⚠️ Not `quote.cities.length > 0`. "No project at all" and "a
                   * project that lists no cities" send a reader to different
                   * screens, which is the whole reason this prop is separate.
                   * `quote.project` being null does not PROVE there is none —
                   * the lookup swallows its own failures — so the component's
                   * copy says "none recorded" rather than asserting.
                   */
                  hasProject={quote.project !== null}
                  currency={quote.currency}
                  companySalesTax={companySalesTax}
                  /*
                   * Read-only while the hand-entered-totals check could not be
                   * run. `linesGate === 'unchecked'` is the failed-read case
                   * and is the one state where the grid renders but must not
                   * write.
                   */
                  canEdit={canEditLines && linesGate === 'open'}
                  canDelete={canDelete && linesGate === 'open'}
                  {...(deleteDenialReason === null ? {} : { deleteDenialReason })}
                  /*
                   * THE REFETCH RULE. Every line write rewrites the quote's four
                   * header totals in the same transaction and returns none of
                   * them, so the grand total at the top of this page is stale
                   * the instant a row saves. Both the quote and this page's own
                   * copy of the basket go back to the server.
                   */
                  onLinesChanged={() => void refetchAfterLineWrite()}
                  /*
                   * ⚠️ **THE REPORT, and it is a SEPARATE prop from the refetch
                   * on purpose.** A line write pushes on the server and answers
                   * `xeroPush`; `onLinesChanged` says the totals moved, which is
                   * a different fact and fires on a delete too, where there is
                   * no report at all.
                   *
                   * The grid hands up the FIRST `failed` report of a save run,
                   * or `undefined` when every push succeeded or was skipped —
                   * its own doc holds that rule. So this renders a refusal and
                   * says nothing otherwise, which is what a quote that is not in
                   * Xero yet needs: `skipped` is the ordinary answer for most of
                   * them, and alarming on it would put a warning on nearly every
                   * line edit in the system.
                   *
                   * ⚠️ It is the SAME strip the form modal and the inline
                   * section report into, deliberately — one surface for one
                   * fact. `autoPushOutcomeOf` answering `null` clears it, and
                   * `null` is the ordinary "say nothing" answer rather than an
                   * error path.
                   */
                  onXeroPush={(report) => setXeroPush(autoPushOutcomeOf(report))}
                />
              </>
            )}
          </PanelBody>
        </Panel>
        </>
            ),
            signature: () => (
        <>
        {/*
          * SIGNATURE.
          *
          * ⚠️ **CORRECTED 2026-08-29 — THE CAUSAL CHAIN THIS ORDER ENCODES IS
          * BROKEN IN THE MIDDLE.** What stood here was: *"the order is the
          * causal chain read downwards — a completed signature accepts this
          * quote; accepting raises an invoice; the invoice is pushed to Xero."*
          *
          * Backend release `20260829011913-5fbab06` removed the middle link:
          * **accepting raises nothing.** A signature still accepts the quote,
          * and an invoice still reaches Xero, but nothing joins them
          * automatically — somebody raises one with Convert or "Bill line
          * items".
          *
          * The ORDER is kept, because signature → invoices → Xero is still the
          * sequence a reader follows and the panels between them still explain
          * each other. **What is no longer true is that it is automatic**, so
          * do not restore any copy or comment describing it as a chain.
          *
          * ⚠️ Placed here, above the invoices and two panels above Xero. A
          * completed signature accepts this quote; the invoice is
          * pushed to Xero. So: what is out for signature → what has been
          * invoiced → what this quote looks like in Xero. The panel's own
          * standing warning names that consequence, and the consequence is
          * literally the next panel on the page.
          *
          * It is deliberately NOT adjacent to `XeroPanel`. Both end in "a real
          * document now exists in the accountant's system", and they are
          * different documents produced by different acts — a signature at the
          * customer's end, a push somebody here pressed. Side by side, the Xero
          * link row reads as the signature's outcome, which it is not: this
          * panel's link row is about the QUOTE's copy in Xero and exists whether
          * anything was ever sent for signature. The invoices panel between them
          * is the thing that actually connects the two, so it sits there.
          *
          * `connection` is omitted on purpose — the read is admin-only and this
          * page is not. See the state block above; the warning renders in its
          * most cautious wording precisely because nothing here can confirm the
          * environment.
          */}
        {/*
          * ⚠️ Wrapped in a `min-w-0` div, and every sibling panel on this page
          * carries that class for the same reason: they are items in a
          * `grid gap-4`, a grid item defaults to `min-width: auto`, and one
          * unbreakable string then widens the whole track instead of wrapping
          * inside it — which is how this page overflowed below 768px before.
          * `EnvelopePanel` takes no `className`, so the wrapper is where the
          * floor goes. The GUID it renders is the string that would do it.
          */}
        <div className="min-w-0">
          <EnvelopePanel
            quoteId={quote.id}
            /* Named the way every other sentence on this page names it. */
            quoteLabel={quote.quoteNumber ? `quote ${quote.quoteNumber}` : quote.subject}
            /*
             * Sending and voiding are `WRITE_ROLES` on the API — the same guard
             * convert and the Xero push run under, and the same one `XeroPanel`
             * below is gated on. Never admin, and never `/permissions/me`, which
             * under-reports the write roles and would hide controls that work.
             *
             * ⚠️ Reading the envelopes takes any authenticated role, so the
             * panel itself renders for a `viewer`. The write controls are
             * withheld, and each says why.
             *
             * ⚠️ **This said "the three write controls" and there are now
             * FOUR** (2026-08-29): `POST /docusign/envelopes/{id}/refresh` —
             * "Check status" — became `WRITE_ROLES` in backend release
             * `20260829123506-c323947`. **A written-down count of gated controls
             * is falsified by adding one, and nothing fails when it is** — the
             * number is deleted rather than corrected, for the same reason the
             * refusal-code counts in `xero.ts` and `line-project.ts` are:
             * **count the call sites in the source when you need the number.**
             */
            canManage={canSend}
            /*
             * The TRUE reason, from the ability model, rather than
             * "administrators only" — which is wrong for a viewer and
             * misleading for a basic user. The panel renders it as text as well
             * as a `title`, because a disabled button has
             * `pointer-events: none` and its tooltip never reaches the person
             * it was written for.
             */
            manageDenial={manageDenial}
            /*
             * All three handlers are passed UNCONDITIONALLY. The panel already
             * gates each control on `canManage` and explains the refusal;
             * passing `undefined` instead would remove the control silently and
             * make the empty state claim sending is "not available from this
             * screen", which is false — it is available from this screen, to
             * somebody with a write role.
             */
            onSend={() => setSendingSignature(true)}
            onRequestSigningLink={setSigningLinkFor}
            onVoid={setVoidingEnvelope}
            /*
             * ⚠️ **NEW on this page.** The panel renders "Change signer and
             * resend" only when handed `onChangeSigner`, and this mount did not
             * pass it — so the control existed on `/quotes` alone.
             *
             * 🔴 **`companyId` travels with it and is not optional in
             * practice.** It defaults to `null`, which is the documented "this
             * quote names no company" case where the signer picker offers
             * nothing and says why — and the panel VOIDS the envelope before
             * this fires. Wiring the callback without the company would buy a
             * destroyed request and an empty picker, leaving the quote with no
             * signature request at all.
             */
            companyId={quote.company?.id ?? null}
            /*
             * The panel owns the VOID half and its dialog carries its own
             * confirmation; what arrives here is the chosen signer, and the
             * SEND that follows is ours. ONE handler for both routes into this
             * — the panel's row control and the header menu.
             */
            onChangeSigner={(_voided, signer) => afterSignerChanged(signer)}
            /*
             * A check came back signed, so the quote is accepted server-side
             * from that moment — see `refetchAfterSignature`.
             *
             * ⚠️ **This used to end "and an invoice may now exist", which is
             * false since backend release `20260829011913-5fbab06`: accepting
             * raises nothing.** The refetch still re-reads the invoices, and
             * deliberately so — Convert and per-line billing can fill that panel
             * from elsewhere while this page sits open through a signing round
             * trip. It is a **re-baseline, not a consequence**, and the
             * distinction is the whole correction: reading the panel afterwards
             * must not be described as observing a result of the signature.
             */
            onSigned={refetchAfterSignature}
            reloadKey={envelopeReloadKey}
          />
        </div>
        </>
            ),
            invoices: () => (
        <>
        {/*
          A LIST, not a single link, and the heading says "raised from" rather
          than "the invoice for this quote".

          `invoices.quote_id` carries no UNIQUE constraint, and that is
          deliberate: staged billing on a large quote legitimately produces
          several invoices, and `createInvoice({ quoteId })` makes more of them on
          purpose. A panel shaped like "the invoice" would show the first row and
          quietly hide the rest — on exactly the records where the money is split
          across several documents and somebody is trying to reconcile it.
        */}
        <Panel className="min-w-0" aria-labelledby="quote-invoices-heading">
          <PanelHeader
            id="quote-invoices-heading"
            title="Invoices raised from this quote"
            /*
              BILL SELECTED LINES — the second way an invoice reaches this panel,
              and it lives HERE rather than in the page header on purpose: this
              is the panel its result arrives in, and the panel a person
              reconciling a part-billed quote is already reading.

              ⚠️ **Offered on every quote, not only staged ones.**
              `POST /quotes/{id}/invoices` never selects `billing_mode` — a
              partial raise is legitimate on a `full` quote and on one that has
              already been converted. Gating it on `billingMode === 'staged'`
              would be this client inventing a rule the server does not have, and
              would leave the control absent on the quotes where somebody most
              often wants to bill a deposit line on its own.

              The disabled branch renders the ability model's own reason as a
              `title`; a `viewer` gets no button at all, since the surface it
              opens is entirely a write.
            */
            actions={
              canBillLines ? (
                <Button
                  variant="secondary"
                  size="sm"
                  leadingIcon={<PlusIcon />}
                  disabled={busy}
                  onClick={() => setPendingPartialBill(true)}
                >
                  Bill line items
                </Button>
              ) : billLinesDenial === null ? undefined : (
                <span className="text-xs text-fg-muted">{billLinesDenial}</span>
              )
            }
            /*
             * The one place the staged fact renders WITHOUT a hover.
             *
             * The convert button's explanation lives in its `title`, and a
             * `title` is not readable on touch — which is where this app is
             * tested at 320px. A permanently disabled button with no visible
             * reason reads as broken, so the panel that a staged quote's
             * invoices actually arrive in states the arrangement outright.
             * ⚠️ Only on a STAGED quote. The other branch used to add "one
             * quote can bill in stages", which is a general fact about quotes
             * and not a fact about this one — commentary, and it went with the
             * rest of it. The staged clause stays because on THIS quote it
             * explains a permanently disabled button.
             */
            description={
              invoicesLoading
                ? 'Loading…'
                : invoicesError !== null
                  ? 'Couldn’t be loaded'
                  : convertIsStaged
                    ? `${pluralise(invoiceCount, 'invoice')} · this quote bills in stages, a line item at a time`
                    : pluralise(invoiceCount, 'invoice')
            }
          />

          {invoicesError !== null && (
            <div
              role="alert"
              className="flex flex-wrap items-start gap-x-2 gap-y-1 border-b border-danger-border bg-danger-soft px-4 py-3 text-xs text-danger"
            >
              <span className="font-semibold">Couldn&rsquo;t load the invoices.</span>
              <span className="min-w-0 [overflow-wrap:anywhere]">{invoicesError}</span>
              <Button variant="secondary" size="sm" onClick={() => void loadInvoices()}>
                Try again
              </Button>
            </div>
          )}

          {invoicesLoading ? (
            <PanelBody>
              <div className="grid gap-2">
                {Array.from({ length: 2 }, (_, i) => (
                  <Skeleton key={i} className="h-4 w-full" />
                ))}
              </div>
            </PanelBody>
          ) : invoices === null ? null : invoices.length === 0 ? (
            <EmptyState
              icon={<QuoteIcon />}
              title="Nothing has been invoiced from this quote"
              /*
               * ⚠️ **FOURTH CORRECTION OF THIS SENTENCE, 2026-08-29**, backend
               * release `20260829011913-5fbab06`. Each of the first three was
               * verified when it landed, and the third said so. Verbatim, the
               * two non-staged branches were:
               *
               *   canConvert: "Converting the quote raises an invoice and copies
               *                these totals onto it. So does moving the quote to
               *                its accepted stage."
               *   otherwise:  "Converting the quote raises an invoice, and so
               *                does moving it to its accepted stage."
               *
               * The CONVERT clause is untouched and still true. The acceptance
               * clause is now false on every quote: **accepting moves the stage
               * and raises nothing.** It was added because an empty panel would
               * otherwise read as proof that accepting was free of consequence —
               * which is exactly what it now is, so the clause simply goes.
               *
               * ⚠️ **`billingMode: 'staged'` is a THIRD branch that stayed**, and
               * it is not about acceptance: convert is refused outright on a
               * staged quote (`409 QUOTE_IS_STAGED`) and invoices come from
               * `POST /quotes/{id}/invoices` one set of lines at a time. Its old
               * wording carried "not by accepting it" as part of a contrast with
               * `full`; with acceptance raising nothing anywhere, the contrast is
               * gone and the sentence now says only what IS true of this quote.
               *
               * The history, kept because the lesson outlived every version of
               * the copy: this sentence has been wrong twice in the DANGEROUS
               * direction. Once it claimed a staged quote raised nothing on
               * convert — a guess, made before `409 QUOTE_IS_STAGED` existed,
               * which described the one destructive button as harmless for
               * thirty minutes. Once it was correct and went stale silently the
               * day the backend added a refusal. **Copy is the one part of a
               * contract nothing typechecks**; say what a route DOES, on the
               * evidence of what is deployed, and re-read it whenever the server
               * changes underneath.
               */
              description={
                convertIsStaged
                  ? 'This quote bills in stages, so Convert is refused. Use “Bill line items”.'
                  : canConvert
                    ? 'Converting raises an invoice and copies these totals onto it.'
                    : 'Converting raises an invoice.'
              }
            />
          ) : (
            <TableWrap frozenHeader frozenMaxHeight="min(60vh, 560px)">
              <Table className="min-w-[560px]">
                <THead>
                  <TR>
                    <TH>Invoice</TH>
                    <TH className="w-40">Status</TH>
                    <TH numeric className="w-40">
                      Grand total
                    </TH>
                    <TH className="w-36">Bill date</TH>
                  </TR>
                </THead>
                <TBody>
                  {invoices.map((invoice) => (
                    <TR key={invoice.id}>
                      <TH scope="row">
                        <Link
                          to={`/invoices/${invoice.id}`}
                          className="rounded-sm text-accent-brand transition-colors duration-150 ease-out hover:underline motion-reduce:transition-none [overflow-wrap:anywhere]"
                        >
                          {invoice.subject}
                        </Link>
                        {invoice.xeroInvoiceNumber && (
                          <span className="mt-0.5 block text-xs text-fg-muted tabular">
                            {invoice.xeroInvoiceNumber}
                          </span>
                        )}
                      </TH>
                      <TD>
                        {/*
                          A null status is a state to RENDER, not to repair.
                          StatusBadge says "No status" rather than inventing a
                          Draft nobody set.

                          ⚠️ It is no longer a normal outcome of CONVERSION: the
                          backend used to resolve the `Draft` row by name and
                          skip it silently when the row was missing, and it now
                          throws a 409 naming the row and rolls the whole
                          conversion back. So a status-less invoice here came
                          from somewhere else — do not write defensive code for
                          a convert that produces one.
                        */}
                        <StatusBadge status={invoice.status} />
                      </TD>
                      <TD numeric>
                        {/*
                          Each invoice carries its OWN currency, and these are
                          never summed into a panel total.

                          ⚠️ The reason given here used to be "there is no
                          exchange rate anywhere in this system", which is no
                          longer true — but the conclusion is unchanged and the
                          real reason is stronger. Each of these is a SEPARATE
                          document with its own currency and its own pinned rate
                          or none at all, so one figure across the column would
                          be an aggregate over mixed currencies: wrong by
                          construction, exactly as the invoices list refuses to
                          show one. Converting a single quote against a single
                          stored rate is not a licence to total a list.
                        */}
                        <InvoiceMoney value={invoice.grandTotal} currency={invoice.currency} />
                      </TD>
                      <TD>
                        {invoice.billDate ? (
                          formatDateOnly(invoice.billDate)
                        ) : (
                          <span className="text-fg-subtle">—</span>
                        )}
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            </TableWrap>
          )}
        </Panel>
        </>
            ),
            /*
             * The Xero panel and the two notes that follow it are one block:
             * both notes say "the panel above", so they move with it. The
             * wrapper keeps today's `gap-4` between the three.
             */
            xero: () => (
        <div className="grid min-w-0 gap-4">
        {/*
          * XERO.
          *
          * Below the invoices, because a Xero quote is a copy of THIS document
          * rather than a record raised from it, and above tasks and notes,
          * which are commentary. One shared panel, driven entirely by the
          * registry in `@/lib/xero` — nothing here branches on the entity.
          *
          * `onRequestLink` is deliberately NOT passed. `quotes.capabilities.link`
          * is true and `POST /xero/link/quotes/{id}` works, but there is no quote
          * SEARCH endpoint — `GET /xero/search/contacts` is contacts-only — so
          * this page has no way to obtain a GUID to link to. The panel offers the
          * button only when a handler exists, which is honest; a picker with
          * nothing to pick from would not be.
          *
          * There is no "Send to Xero" here either, and there must not be.
          *
          * ⚠️ **CORRECTED 2026-08-31 — THE REASON CHANGED, AND THE OLD ONE WAS
          * INCIDENTAL.** It read: *"`quotes.capabilities.sync` is false because
          * a quote is pushed inline and the worker refuses quote jobs outright,
          * answering 409 `XERO_PUSH_IS_INLINE`. The panel already withholds
          * it."* Every word of that is still true and none of it is a decision
          * about whether a PERSON may press a button — it is a fact about which
          * endpoint serves the push. Flip that capability the day `/xero/sync`
          * learns to queue quotes and "Send to Xero" reappears here on its own.
          *
          * The reason now is the owner's, 2026-08-31, asked twice: *"remove
          * this Push to Xero button from both invoices and quotes, because auto
          * saving is working."* `XeroPanel` withholds Create, Send AND Retry on
          * `CREATED_BY_SAVING`, which records the decision rather than the
          * capability.
          *
          * 🔴 **So there is no manual retry for a quote whose push failed.**
          * The retry is to save the quote again. A quote whose push failed and
          * which nobody edits again stays stale in Xero with nothing on screen
          * offering to resend it — accepted, not overlooked.
          *
          * ⚠️ **AND THERE IS NO "Create in Xero" EITHER, AS OF 2026-08-30.**
          * Owner's decision: saving a quote creates it in Xero as well as
          * updating it, so the panel withholds the create for quotes and
          * invoices too (`CREATED_BY_SAVING` in `XeroPanel`) and says in one
          * line that the save is what sends it. What is left here is the
          * status, the refusal routing, and Unlink.
          *
          * ⚠️ `POST /xero/create/quotes/{id}` is UNCHANGED and stays as the
          * operator recovery path — the affordance went, not the capability.
          */}
        <XeroPanel
          entity="quotes"
          recordId={quote.id}
          recordName={quote.subject}
          link={xeroLink}
          connection={xeroConnection}
          /*
           * Every remaining action in this panel — Unlink, and resolving a
           * conflict — is a write on the QUOTE, and writes take any write role,
           * the same guard convert runs under. Not admin, and never
           * `/permissions/me`, which under-reports those roles.
           *
           * ⚠️ It said *"Creating this quote in Xero is a write on the QUOTE"*
           * until 2026-08-30. There is no create here now; the gate is
           * unchanged because the actions left need exactly the same role.
           */
          canManage={canSend}
          /*
           * Routes a `company-record` refusal at the record that actually has
           * to change. Both Sales-defaults codes point here, and the reader's
           * instinct — the error arrived while they were looking at a quote —
           * is to go and edit the quote, which cannot help.
           */
          company={quote.company}
          /*
           * The page owns the dialog; the panel only asks for it, and only for
           * the two codes a Sales-defaults form can actually fix.
           */
          onFixCompanyDefaults={openXeroDefaults}
          onChanged={() => void loadQuote()}
        />

        {/*
          * What happened after the defaults were saved, and — the load-bearing
          * half — what did NOT happen.
          *
          * The panel above still shows the refusal that opened the dialog: it
          * holds that banner until the next attempt, and it has to, because
          * nothing has been retried. Saying so beside it is the difference
          * between "save it again" and someone reading a stale red block as the
          * save having failed. (It read *"press Create again"* until 2026-08-30;
          * the create button is gone from quotes and the retry is a save now.)
          *
          * It sits outside the panel rather than inside because the panel is
          * another agent's file, and it is a note rather than a Panel because
          * it is transient — it is cleared the next time the dialog opens.
          */}
        {defaultsSaved && (
          <p
            role="status"
            className="m-0 min-w-0 rounded-md border border-success-border bg-success-soft px-3 py-2 text-xs text-success [overflow-wrap:anywhere]"
          >
            {defaultsSaved}
          </p>
        )}

        {/*
          WHAT THE LAST SAVE DID TO XERO'S COPY — and it always says the save
          itself succeeded, on every branch.

          ⚠️ **The two halves are rendered TOGETHER, never coalesced.** A save
          that succeeded and a push that was refused are two things that are
          true at the same time, and `saved ?? refused` — picking whichever is
          "more important" — is the silent drop CLAUDE.md catalogues. Somebody
          who reads only "Xero refused this" concludes their edit is lost and
          types it again.

          ⚠️ **`settled` is toned as INFORMATION, not as a failure, and that is
          the most consequential decision on this block.** Once anyone sends the
          quote from inside Xero its copy leaves DRAFT and every later push is
          refused — which is the design working, and which will be the COMMON
          answer, not the exception. A red banner on every save afterwards
          trains people to disregard the Xero notice, and the refusals that
          genuinely need acting on go with it.

          `RefusalBanner` is imported from the panel rather than reproduced, so
          a refusal routes the reader to the same place whether it came from a
          create they pressed or a push their save triggered. The two neutral
          outcomes deliberately do not use it — it tones `destination:
          'nowhere'` as terminal with `role="alert"`, which is right for a
          button someone pressed and wrong for both of these.
        */}
        {xeroPush !== null && skipWorthShowing && (
          <div className="min-w-0 space-y-2">
            {xeroPush.kind === 'updated' ? (
              <p
                role="status"
                className="m-0 min-w-0 rounded-md border border-success-border bg-success-soft px-3 py-2 text-xs text-success [overflow-wrap:anywhere]"
              >
                Saved, and Xero&rsquo;s copy of this quote was updated to match.
              </p>
            ) : xeroPush.kind === 'settled' ? (
              <div
                role="status"
                className="min-w-0 rounded-md border border-border bg-surface-sunken px-3 py-2 text-xs text-fg-secondary"
              >
                <p className="m-0 font-semibold text-fg">
                  Saved here. Xero&rsquo;s copy was not changed.
                </p>
                <p className="m-0 mt-1 [overflow-wrap:anywhere]">
                  It has moved past draft, so make any change the customer needs in Xero.
                </p>
                {xeroPush.messages.length > 0 && (
                  <p className="m-0 mt-1 text-fg-muted [overflow-wrap:anywhere]">
                    {xeroPush.messages.join(' ')}
                  </p>
                )}
              </div>
            ) : xeroPush.kind === 'gone' ? (
              /*
                Routed through the same `STOOD_DOWN_AS_GONE` model the panel
                uses, and deliberately NOT repeating its recovery. The panel
                directly above is already showing the stood-down link and the
                "unlink, then create afresh" sequence against the Unlink button
                that performs it; a second copy here would be one instruction in
                two places, and the one further from the button is the one that
                goes stale.
              */
              <div
                role="status"
                className="min-w-0 rounded-md border border-warning-border bg-warning-soft px-3 py-2 text-xs text-warning"
              >
                <p className="m-0 font-semibold">
                  Saved here. The quote was deleted in Xero, so there was nothing to update.
                </p>
                <p className="m-0 mt-1 [overflow-wrap:anywhere]">
                  The link above has been stood down.
                </p>
              </div>
            ) : xeroPush.kind === 'skipped' ? (
              /*
                ⚠️ **NOT A FAILURE, and toned so that nobody could read it as
                one.** Nothing was sent to Xero and nothing went wrong; the
                same neutral surface `settled` uses, no alert glyph, no
                warning colour and no action offered — there is nothing here
                for anyone to put right.

                Only reached on a LINKED quote — see `skipWorthShowing`, which
                draws nothing at all for the ordinary "this quote is not in
                Xero yet" skip that every save of a new quote produces.

                The server's own sentence is the body. It is stated as safe to
                show, and it is the only thing that knows WHY.
              */
              <div
                role="status"
                className="min-w-0 rounded-md border border-border bg-surface-sunken px-3 py-2 text-xs text-fg-secondary"
              >
                <p className="m-0 font-semibold text-fg">
                  Saved here. Nothing was sent to Xero this time.
                </p>
                <p className="m-0 mt-1 [overflow-wrap:anywhere]">{xeroPush.reason}</p>
              </div>
            ) : xeroPush.kind === 'unreachable' ? (
              <div
                role="status"
                className="min-w-0 rounded-md border border-warning-border bg-warning-soft px-3 py-2 text-xs text-warning"
              >
                <p className="m-0 font-semibold">
                  Saved here. We couldn&rsquo;t tell whether Xero was updated.
                </p>
                {/*
                  ⚠️ States the UNCERTAINTY rather than resolving it. The
                  request may well have landed — an answer never arrived, which
                  is not the same as nothing happening — so no sentence here
                  may describe Xero's copy, and none may invite a retry that
                  could be a second write.
                */}
                <p className="m-0 mt-1 [overflow-wrap:anywhere]">
                  {xeroPush.messages.join(' ')} Check the quote in Xero before sending it.
                </p>
              </div>
            ) : (
              <>
                <p
                  role="status"
                  className="m-0 min-w-0 rounded-md border border-border bg-surface-sunken px-3 py-2 text-xs text-fg-secondary [overflow-wrap:anywhere]"
                >
                  <span className="font-semibold text-fg">Saved here.</span> Xero&rsquo;s copy was
                  not updated.
                </p>
                {/*
                  `refusal === null` is a code newer than `XERO_QUOTE_REFUSALS`,
                  and the fallback is the server's own message with NO
                  destination invented for it. Guessing one — the quote form,
                  because most refusals point there — would send someone to edit
                  a quote over a problem in Xero's settings with no way to tell
                  the guidance was made up.
                */}
                {xeroPush.refusal !== null ? (
                  /*
                   * `alreadyInXero` is read from the link row rather than
                   * hard-coded `true`, and 2026-08-30 is why that was worth
                   * doing. The comment here used to say this banner *"is only
                   * reached from the automatic push, which only runs on a
                   * linked quote, so `true` would be correct today"* — and the
                   * automatic push it named is deleted. The refusal now arrives
                   * on the SERVER's report of a save, which is answered for
                   * every quote, linked or not.
                   *
                   * Reading the fact costs nothing and cannot go stale;
                   * asserting it would have been a claim that had to be
                   * re-checked by whoever changed where this block is reached
                   * from, which is exactly the check nobody knows to make.
                   */
                  <RefusalBanner
                    code={xeroPush.code}
                    refusal={xeroPush.refusal}
                    origin={xeroPush.origin}
                    lines={xeroPush.messages}
                    company={quote.company}
                    alreadyInXero={xeroLink.id !== null}
                    onFixCompanyDefaults={openXeroDefaults}
                  />
                ) : (
                  <div
                    role="alert"
                    className="min-w-0 rounded-md border border-warning-border bg-warning-soft px-3 py-2 text-xs text-warning"
                  >
                    {xeroPush.messages.map((line) => (
                      <p key={line} className="m-0 [overflow-wrap:anywhere]">
                        {line}
                      </p>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}
        </div>
            ),
          }}
        />
      </div>

      {/*
        Tasks and notes used to close this page as two panels, which is the one
        place nobody scrolls to. They are now one button in the header away, in
        a sheet over the quote rather than below it.
      */}
      <ActivityDrawer
        open={activityOpen}
        onClose={() => setActivityOpen(false)}
        target={activityTarget}
        recordLabel={activityLabel}
        viewerId={viewerId}
        viewerTimeZone={viewerTimeZone}
      />

      <QuoteFormModal
        open={editing}
        onClose={() => setEditing(false)}
        quote={quote}
        /*
         * ⚠️ `lines === null` is "not loaded yet", NOT "no lines".
         *
         * This read `lineCount > 0`, and `lineCount` is `lines?.length ?? 0` —
         * so a basket still loading, or one whose fetch failed, reported "no
         * lines" and the form offered the four header-total inputs on a quote
         * that has them. Editing one there writes it literally (the server
         * never re-derives), the header then disagrees with its own lines, and
         * `convert` copies all four onto the invoice verbatim. The figures it
         * replaces are kept nowhere.
         *
         * `'unknown'` locks the totals instead, which is the safe direction:
         * the cost of being wrong that way is a person who has to close the
         * dialog and open it again.
         */
        hasLineItems={lines === null ? 'unknown' : lines.length > 0}
        /*
         * ⚠️ **CORRECTED 2026-08-30. The prop no longer causes a push — it
         * only carries the SERVER'S report of one.**
         *
         * It used to read `onXeroAutoPush={pushQuoteToXero}` and its note said:
         *
         * > *"The push exists here because the REPORT exists here. The prop is
         * > a callback rather than a flag exactly so a caller with nowhere to
         * > render the outcome cannot switch it on — `QuotesPage` mounts the
         * > same form and passes nothing, so a save from the list does not push
         * > and claims nothing."*
         *
         * The push is no longer this app's to withhold: the PATCH itself pushes
         * on the server, so `QuotesPage` saving a quote now reaches Xero too,
         * whether or not it passes this. What the prop still buys is the half
         * that is still ours — a caller with nowhere to render the outcome does
         * not get handed one, and `QuotesPage` still claims nothing.
         *
         * ⚠️ **`undefined` here would be silence, not safety.** With the push
         * server-side, omitting this does not stop anything reaching Xero; it
         * only stops the person being told what happened.
         *
         * `null` from `autoPushOutcomeOf` is the ordinary answer for a server
         * that sent no report and for a skip on a quote that is not in Xero —
         * see the render block below, which draws nothing for it.
         */
        onXeroPush={(report) => setXeroPush(autoPushOutcomeOf(report))}
        /*
         * Re-read rather than trusting the returned record. A header PATCH and a
         * line write are two competing writers of the same four totals, and a
         * refetch is the only thing that reconciles them if both happened.
         *
         * A CONVERTED quote is still fully patchable, and patching it never
         * touches the invoice it produced — so nothing here goes read-only once
         * an invoice exists, and an edit must never re-run convert to "sync" the
         * two. They are separate documents from the moment of conversion.
         */
        /*
         * ⚠️ **The INVOICES are re-read too**, and the reason changed on
         * 2026-08-29, backend release `20260829011913-5fbab06`.
         *
         * This used to say that the stage field in this form is one of the two
         * human routes that ACCEPT a quote, that accepting raises an invoice and
         * pushes it to Xero as a draft, and that `QuoteFormModal` owned the
         * `409 QUOTE_ACCEPTANCE_REQUIRES_CONFIRMATION` handshake around it. None
         * of that is true now: **accepting moves the stage and nothing else**,
         * and the handshake is deleted.
         *
         * The read is KEPT because the panel it feeds can still go stale under
         * an open form — Convert and `POST /quotes/{id}/invoices` both raise
         * invoices, and a save here is as good a moment as any to re-baseline.
         * Re-reading only the quote would leave "Invoices raised from this quote"
         * empty and Convert enabled, which invites a conversion that is a 409.
         */
        onSaved={() => {
          setEditing(false);
          void loadQuote();
          void loadInvoices();
        }}
        /*
         * ⚠️ **THE GRID INSIDE THIS FORM SAVES ON ITS OWN, AND THAT RUN REACHED
         * NOTHING ON THIS PAGE.** Identical to `InvoiceDetailPage`, and fixed in
         * the same pass — read the longer note there.
         *
         * The short of it: press the grid's own "Save N rows", and the footer
         * Save correctly greys out with nothing left to send, so the only way
         * out is **Cancel** — which never runs `onSaved`. Nor does the X,
         * Escape or the overlay. The page behind the dialog then keeps its old
         * line rows, the project on each of them, and its four header totals,
         * which a line write rewrites in its own transaction and returns none
         * of.
         *
         * ⚠️ **`loadInvoices()` is deliberately NOT called here**, though
         * `onSaved` above does call it. Nothing a line PATCH does raises or
         * removes an invoice: Convert and `POST /quotes/{id}/invoices` do that,
         * and both already re-read. Re-reading it on every grid save would be a
         * request per save that can only ever return what it returned before.
         */
        onLinesSaved={() => void refetchAfterLineWrite()}
      />

      {/* The PDF viewer — mounted once for the whole page. */}
      {preview.element}

      {/*
        SEND FOR SIGNATURE.

        Mounted conditionally rather than held at `open={false}` — the form takes
        no `open` prop, so a fresh mount is the reset, and there is no effect
        that could forget a field on reopen.

        ⚠️ The modal closes ITSELF after a successful send, so `onSent` must not
        also close it; what it has to do is tell the panel to re-read, because
        the panel takes a `reloadKey` and not a row.
      */}
      {sendingSignature && (
        <SendForSignatureModal
          quote={quote}
          /*
           * 🔴 **The signer chosen in "Change signer and resend", passed
           * through.** Omitting it re-seeds the company's primary contact into
           * an untouched signer field — very often the exact person somebody
           * has just decided not to send to — so the operation silently undoes
           * itself while looking like it worked.
           *
           * Spread rather than passed as `undefined`: an explicitly empty
           * `SignerValue` would SUPPRESS the ordinary primary-contact seed, so
           * a plain Send must not hand the prop over at all.
           */
          {...(signatureSeed ? { initialSigner: signatureSeed } : {})}
          onClose={() => {
            setSendingSignature(false);
            // The seed dies with the dialog: a send abandoned here must not
            // pre-fill the next one with a signer from an operation nobody
            // completed.
            setSignatureSeed(null);
          }}
          onSent={(envelope) => {
            setSendingSignature(false);
            setSignatureSeed(null);
            setEnvelopeReloadKey((key) => key + 1);
            /*
             * ⚠️ **On `embedded` delivery DocuSign emails NOBODY.** The link is
             * ours to hand over, so a send that ends with the dialog closed and
             * nothing else on screen leaves an envelope outstanding that the
             * signer has never been told about — and the person who sent it has
             * no reason to think a further step exists. Chaining straight into
             * the link dialog is that step, and it is the moment to do it: the
             * link is single use and dies in about five minutes, so it is minted
             * with the signer in front of you or not at all.
             *
             * On `email` delivery DocuSign has already written to the signer and
             * there is no link to mint, so nothing opens.
             *
             * `isEmbeddedDelivery` is the shared rule rather than an
             * `=== 'embedded'` written here; it is also the narrowing the
             * signing-URL call demands, so this cannot route an emailed envelope
             * into a mint.
             */
            if (isEmbeddedDelivery(envelope)) setSigningLinkFor(envelope);
          }}
        />
      )}

      {/*
        CHANGE SIGNER AND RESEND — the VOID half, for the HEADER MENU's route.

        ⚠️ **Two mounts of this dialog on one page, and that is not a
        duplicate.** `EnvelopePanel` mounts its own copy internally, driven by
        its row control and its own state, which this page cannot reach; this
        one is driven by the header menu. Only ever one is open, each opened by
        its own trigger, and **both finish in the same `afterSignerChanged`** —
        which is where the part that must not be got twice lives.

        ⚠️ **No confirmation is added here, deliberately.** `ChangeSignerDialog`
        already carries its own `ConfirmDialog` — `tone="danger"`, naming the
        signer and the document, stating that the void is final, that any
        signature already on it is discarded, and that nothing is sent until the
        send form is completed, so the quote has no outstanding request until
        then. A second confirmation in front of that would be two dialogs for
        one action, which is worse than none.

        🔴 **`companyId` defaults to `null`** — the documented "names no
        company" case, where the signer picker offers nothing and says why,
        while the void has already run. The menu refuses the action outright on
        a company-less quote rather than relying on this.
      */}
      {changingSignerFor !== null && (
        <ChangeSignerDialog
          open
          envelope={changingSignerFor}
          companyId={quote.company?.id ?? null}
          onClose={() => setChangingSignerFor(null)}
          /*
           * The voided envelope itself is not kept: this page holds no copy of
           * the list to patch — the header and the panel both read through
           * `envelopeReloadKey` — so the re-read inside the handler replaces it.
           */
          onVoided={(_voided, signer) => afterSignerChanged(signer)}
        />
      )}

      {/*
        THE SIGNING LINK — a credential, not a page reference.

        Mounted only while there is an envelope for it, so closing UNMOUNTS the
        dialog and the minted URL dies with it. That is the dialog's own design
        and this call site keeps it: the link is never lifted into page state,
        never put in the URL, and never held anywhere it could outlive the panel
        that minted it.
      */}
      {signingLinkFor !== null && (
        <SigningLinkDialog
          open
          envelope={signingLinkFor}
          onClose={() => setSigningLinkFor(null)}
          /*
           * Minting is `WRITE_ROLES`, the same question the panel asks — and the
           * dialog is only reachable from a control the panel already gated, so
           * this is belt and braces rather than the only check.
           */
          canMint={canSend}
          mintDenial={manageDenial}
          /*
           * The dialog can refresh the envelope itself, and a refresh that
           * observes a signature has already moved the quote's stage. Two things
           * are stale from that moment and both are re-read: the panel's list
           * (the row's status changed) and the quote itself (it may now be
           * accepted, with an invoice against it).
           *
           * ⚠️ `signed` is the server's own computed field, not a status string
           * compared here. A quote is re-read only when it is true — a refresh
           * that came back `sent` changed nothing about the quote and two
           * needless requests would be paid on every press.
           */
          onEnvelopeRefreshed={(envelope) => {
            setEnvelopeReloadKey((key) => key + 1);
            if (envelope.signed) refetchAfterSignature();
          }}
        />
      )}

      {/*
        VOID.

        ⚠️ **No stale-copy callback was added, and the reason is that the close
        handler is a better answer than a new prop.** The dialog's `409` branch —
        something reached a terminal state after the screen was drawn — keeps the
        dialog open showing the server's own sentence and never calls `onVoided`,
        which leaves this page's copy of that envelope known-stale. An
        `onRefused` prop would fix it, and would mean editing a component this
        page does not own to carry information the page can get for nothing.

        So the list is re-read on EVERY close instead: the success path, the 409
        path, and a plain Cancel. One request, on a dialog somebody deliberately
        opened, and it cannot be wrong the way a callback that has to be
        remembered can. `onVoided` bumps as well rather than relying on the close
        that follows it, so the re-read is not hostage to the ordering of two
        callbacks in a file this page does not control.

        ⚠️ **The QUOTE is deliberately NOT re-read here.** A 409 may mean the
        envelope completed, but a void is not the request that observes a
        signature — `POST /docusign/envelopes/{id}/refresh` is, and it is the
        only thing that moves the quote. Re-reading the quote on a void would
        imply this page can tell, which it cannot. What the reader gets instead
        is the refreshed row with its true status and the panel's own Check
        status button beside it, which is the sanctioned route and the one that
        fires `onSigned`.
      */}
      {voidingEnvelope !== null && (
        <VoidEnvelopeDialog
          open
          envelope={voidingEnvelope}
          onClose={() => {
            setVoidingEnvelope(null);
            setEnvelopeReloadKey((key) => key + 1);
          }}
          onVoided={() => {
            setVoidingEnvelope(null);
            setEnvelopeReloadKey((key) => key + 1);
          }}
        />
      )}

      {/*
        THE SALES-DEFAULTS DIALOG, and its own three states.

        It takes a whole `Company` — three of the fields it reads are absent
        from `quote.company`, which is a bare `{id, name}` — so it can only be
        rendered once the fetch has landed. Loading and failure are NOT the
        dialog with blanks in it: they are their own surface, because "we could
        not read the customer record" and "the customer record has no tax rate"
        are different problems with different next steps.
      */}
      {defaultsCompany !== null ? (
        <XeroSalesDefaultsDialog
          open={defaultsOpen}
          company={defaultsCompany}
          /*
           * `missing` is deliberately omitted. The panel owns the refusal and
           * does not report which code came back, so naming one would be a
           * guess; left out, the dialog derives it from the company's own nulls,
           * which is the same answer in every ordinary case and is never wrong.
           */
          context={
            <>
              Opened from {quote.quoteNumber ? `quote ${quote.quoteNumber}` : quote.subject}.
            </>
          }
          onClose={closeXeroDefaults}
          /*
           * NO AUTO-RETRY, deliberately. One push was asked for; a second one
           * firing by itself is an action nobody took, and a refusal from it
           * would arrive on a screen with nothing explaining where it came from.
           *
           * The saved company is written back before closing so the dialog does
           * not flip to its loading surface on the way out — and so the values
           * on screen while it fades are the ones that were actually stored.
           */
          onSaved={(saved) => {
            setDefaultsResult({ id: saved.id, company: saved, error: null });
            setCompanyTax({
              companyId: saved.id,
              rate: saved.xeroSalesTaxRate,
              taxType: saved.xeroSalesTaxType,
            });
            setDefaultsOpen(false);
            /*
             * ⚠️ **CORRECTED 2026-08-30.** It said: *"Create this quote in Xero
             * again from the panel above."* There is no Create button on a quote
             * any more — the save is what sends it — so that sentence pointed at
             * a control the reader would not find, which is worse than no
             * sentence: they would conclude the panel had failed to render.
             */
            setDefaultsSaved(
              `${saved.name} now carries its Xero sales defaults. Save this quote again to send it to Xero.`,
            );
          }}
        />
      ) : (
        <Modal
          open={defaultsOpen}
          onClose={closeXeroDefaults}
          title="Xero sales defaults"
          size="sm"
          footer={
            <Button variant="secondary" onClick={closeXeroDefaults}>
              Close
            </Button>
          }
        >
          {defaultsCompanyId === null ? (
            /*
             * Not reachable from the refusal this dialog answers — a quote with
             * no company is refused with `XERO_QUOTE_NO_COMPANY` first — but the
             * opener is a plain button, so the state is expressible and gets a
             * sentence rather than an empty dialog.
             */
            <p className="m-0 text-sm text-fg-secondary">
              Set a company on the quote first — the defaults live on the customer record.
            </p>
          ) : defaultsLoading ? (
            <div className="grid gap-2" aria-busy="true">
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="h-9 w-full" />
              <Skeleton className="h-9 w-full" />
            </div>
          ) : (
            <div className="space-y-3">
              <div
                role="alert"
                className="flex flex-col items-start gap-1 rounded-md border border-danger-border bg-danger-soft px-3 py-2 text-xs text-danger"
              >
                <span className="font-semibold">Couldn&rsquo;t load the customer record.</span>
                <span className="min-w-0 [overflow-wrap:anywhere]">
                  {defaultsError ?? 'The request didn’t complete.'}
                </span>
              </div>
              <p className="m-0 text-xs text-fg-muted">Nothing was changed.</p>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    // Cleared here, not in the effect: without it the failure
                    // sits on screen through the whole retry, which reads as
                    // the retry having failed instantly.
                    setDefaultsResult(null);
                    setDefaultsToken((token) => token + 1);
                  }}
                >
                  Try again
                </Button>
                {quote.company && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => navigate(`/companies/${quote.company?.id}`)}
                  >
                    Open {quote.company.name}
                  </Button>
                )}
              </div>
            </div>
          )}
        </Modal>
      )}

      {/*
        The hard half of the totals guard. A banner alone is not enough: this is
        a one-way, unconfirmed, unlogged overwrite of the only copy of these
        figures, so the dialog names all four at the moment of the decision.
      */}
      <ConfirmDialog
        open={pendingTotalsAdd}
        onClose={() => setPendingTotalsAdd(false)}
        onConfirm={() => {
          setPendingTotalsAdd(false);
          /*
           * Acknowledging MOUNTS the editable grid; it does not add a row. The
           * grid has its own "Add row" button and its own save step, so nothing
           * is written until the person actually enters a line — which means
           * they can still back out after reading this.
           */
          setTotalsAcknowledged(true);
        }}
        title="Replace the totals on this quote?"
        description={
          <>
            <p className="m-0">
              These figures were entered by hand. Adding a line replaces all four with values from
              the lines.
            </p>
            <HeaderTotals quote={quote} />
            <p className="m-0 mt-3">The current figures cannot be recovered.</p>
          </>
        }
        confirmLabel="Add line items anyway"
      />

      {/*
        ⚠️ **THE CONFIRMATION DIALOG THAT STOOD HERE IS GONE, AND IT IS NOT A
        REGRESSION.** On the owner's instruction converting no longer creates the
        invoice on a confirm — it opens this prefilled form, and nothing is
        written until Save. Everything the dialog used to state is now either
        visible in the form itself (the four totals, the dates, the counterparty)
        or said inside it (the line items are the quote's own, copied by the
        server with their projects, cities and campaign inclusions).

        ⚠️ **The due date needed stating before; it does not now.** The old
        dialog had to say "30 days after its bill date" as a RULE, because the
        server picked the date and this page would have been guessing at it. The
        form shows the actual date, in an editable field, before anything exists
        — a stronger answer than the sentence it replaces.

        ⚠️ **The stage move is the fact still not visible anywhere on the form.**
        Convert sets the accepted stage internally and — the backend has
        confirmed — does NOT re-trigger the acceptance hook, so this is the one
        route to that stage which raises no second document. That is said in the
        totals panel above, beside the figures it applies to, and it must not be
        lost: to anybody who knows what accepting a quote normally costs, an
        unexplained stage move reads as a promise of a second invoice.

        ⚠️ **Do not put a ConfirmDialog back in front of this.** Two
        confirmations for one action is how somebody learns to click through the
        first one.
      */}
      <InvoiceFormModal
        open={pendingConvert}
        onClose={() => setPendingConvert(false)}
        convertFrom={quote}
        /*
         * `POST /quotes/{id}/convert` hands back an INVOICE, never a quote, and
         * this page reads fields the two do not share — so it is navigated to
         * rather than fed into any state here. The modal calls this once the
         * conversion (and any follow-up edit) has landed, and also when somebody
         * closes it after a conversion whose follow-up was refused: the invoice
         * exists either way, and walking away from it silently is the one
         * outcome that produces a second one.
         */
        /*
         * ⚠️ **No `onXeroPush`, and the conversion's Xero report lives INSIDE the
         * modal — 2026-09-13.** `POST /quotes/{id}/convert` pushes the INVOICE it
         * creates (not this quote) and answers `xeroPush` beside `data`. On a
         * `failed` the server writes nothing and re-reads nothing, so the new
         * invoice arrives with no Xero link and its own page cannot tell that from
         * an invoice nobody has pushed yet — **that report is the only artefact
         * anywhere carrying the fact.**
         *
         * So `InvoiceFormModal` reads it and **stays open** on a refusal, exactly
         * as it already does for a refused added line, and hands the invoice over
         * when the person presses Done. ⛔ **This page must not report it**:
         * `afterConvert` navigates, so anything set here is unmounted before it is
         * read. `xeroPush` state on this page is for SAVES of this quote, which is
         * a different document. See `conversionPushReport`.
         */
        onSaved={afterConvert}
      />

      {/*
        BILLING CHOSEN LINES — `POST /quotes/{id}/invoices`.

        ⚠️ **Not a variant of Convert, and mounted separately for that reason.**
        Convert bills the whole document once and is then spent; this bills the
        lines somebody ticks and stays available until every line is taken. It
        does **not** move the quote's stage, it carries **no distinct error code**
        on any refusal, and it is **not gated on `billingMode`** — all three are
        the opposite of Convert, which is why the two share no code here.

        The modal owns the request and every refusal. On success the server hands
        back a whole invoice with `Location` into `/invoices`, so it is navigated
        to exactly as a conversion is — `afterConvert` is reused because the
        onward step really is identical, not because the two actions are.

        ⚠️ **It owns the Xero REPORT too, as of 2026-09-13, and for the same
        reason it owns the refusals.** The endpoint pushes the INVOICE it raised
        and answers `xeroPush` beside `data`; a `failed` push writes nothing
        server-side, so the new invoice arrives with no Xero link and nothing on
        its own page distinguishes that from an invoice nobody has pushed yet.
        The modal holds itself open to say so and only then calls `onRaised` —
        which is what it has to do, because `onRaised` navigates and would take
        any band on this page with it. See `conversionPushReport`.
      */}
      <BillQuoteLinesModal
        /*
         * ⚠️ **A REMOUNT, and it is what makes the dialog start clean.** The
         * modal holds a selection, three typed fields and — the one that must
         * not survive — the previous attempt's refusal. Keying on the open flag
         * gives every opening its own instance, so nothing carries over and the
         * component needs no reset effect, which would clear those a render late
         * and show the stale refusal in between.
         */
        key={pendingPartialBill ? 'bill-lines-open' : 'bill-lines-closed'}
        open={pendingPartialBill}
        quote={quote}
        onClose={() => setPendingPartialBill(false)}
        onRaised={afterConvert}
      />

      {/*
        The line-delete confirmation lives in `QuoteLineItemsTable` now, and it
        says more than this one did: it names the line's product and city, counts
        the campaign inclusions that go with it, and — the part this page could
        not do — warns when the line has already been BILLED. The backend has no
        guard there at all (`f_invoice_products.quote_product_id` is ON DELETE
        SET NULL, no 409, no count), so that warning is the only protection there
        is, and it has to sit where `invoiced` is in hand.
      */}

      <ConfirmDialog
        open={pendingDelete}
        onClose={() => {
          setPendingDelete(false);
          setActionError(null);
        }}
        onConfirm={() => void removeQuote()}
        title={`Delete ${quote.subject}?`}
        /*
         * TWO consequences the API does NOT protect against, and one it does.
         *
         * The one it does: a quote with DocuSign signature envelopes against it
         * is a 409, not a 204. `f_docusign_envelopes.quote_id` is ON DELETE
         * RESTRICT, because an envelope and any signature on it live inside
         * DocuSign independently of this row — deleting locally would leave a
         * legal record with nothing saying what it settles. `removeQuote` shows
         * that refusal verbatim and leaves this dialog open.
         *
         * A conversion, by contrast, is no obstacle at all: a converted quote
         * still deletes, so nothing server-side guards the provenance and this
         * dialog is the only thing standing between somebody and both of these.
         * Both counts are already loaded here, so both are named.
         *
         *  1. the line items cascade away with the quote;
         *  2. every invoice raised from it SURVIVES, with its `quote` set to
         *     null. The FK is ON DELETE SET NULL and nothing else records where
         *     the invoice came from, so the link between the offer and the bill
         *     is gone for good and is afterwards indistinguishable from an
         *     invoice that never had a quote at all.
         */
        description={
          <>
            <p className="m-0">
              {lines === null
                ? 'This permanently deletes the quote and any line items on it.'
                : lineCount === 0
                  ? 'This quote has no line items.'
                  : `This also deletes the ${pluralise(lineCount, 'line item')} on the quote.`}
            </p>
            <p className="m-0 mt-2">
              {invoiceTotal === null
                ? 'Any invoice raised from this quote survives, but loses its link back to it.'
                : invoiceCount === 0
                  ? 'No invoice has been raised from this quote.'
                  : `The ${pluralise(invoiceCount, 'invoice')} raised from this quote ${
                      invoiceCount === 1 ? 'survives' : 'survive'
                    }, but ${
                      invoiceCount === 1 ? 'loses its link' : 'lose their links'
                    } back to it.`}
            </p>
            {/* The tasks and notes cascade with the quote exactly as the lines
                do, and the API names neither — so they are named here. */}
            <p className="m-0 mt-2">
              {attachmentsSentence(
                attachmentTotals.tasks,
                attachmentTotals.notes,
                attachmentTotals.systemNotes,
              )}
            </p>
            {/* The delete never reaches Xero (backend `deleteQuote`): the Xero quote and its link survive. */}
            <p className="m-0 mt-2">
              If this quote is in Xero, it stays there. Only the SEED EMERGE record is deleted, and
              that cannot be undone.
            </p>
          </>
        }
        confirmLabel="Delete quote"
        loading={busy}
        error={actionError}
      />
    </>
  );
}
